import { captureException } from '@sentry/nextjs';
import { useMemo, useEffect, useRef, useState, useContext } from 'react';
import { InferGetStaticPropsType, GetStaticProps, GetStaticPaths } from 'next';
import { NextSeo, ProductJsonLd, VideoJsonLd } from 'next-seo';
import Breadcrumb from 'src/components/Breadcrumb';
import BreadcrumbItem from 'src/components/BreadcrumbItem';
import { badgeKinds, DEFAULT_PAGINATION, MasterCurrency, UITimeFormat, ZERO_AMOUNT } from 'src/lib/constants';
import { AdminRoutes, ApiRoutes, AppRoutes } from 'src/lib/routes';
import { BsStarFill } from 'react-icons/bs';
import User1Svg from 'public/images/svg_icons/user-1.svg';
import { HiOutlinePlusCircle } from 'react-icons/hi';
import { FiX } from 'react-icons/fi';
import CheckInCircleSvg from 'public/images/svg_icons/check_in_circle.svg';
import CloseInCircleSvg from 'public/images/svg_icons/close_in_circle.svg';
import PinPointIcon from 'public/images/experience/icon_pinpoint.png';
import ClockIcon from 'public/images/experience/icon_clock.png';
import UserIcon from 'public/images/experience/icon_user.png';
import CarIcon from 'public/images/experience/icon_car.png';
import GiftBoxIcon from 'public/images/experience/icon_giftbox.png';
import SuitcaseIcon from 'public/images/experience/icon_suitcase.png';
import WatchIcon from 'public/images/experience/icon_watch.png';
import FlagIcon from 'public/images/experience/icon_flag.png';
import TicketIcon from 'public/images/experience/icon_ticket.png';
import ShieldIcon from 'public/images/experience/icon_shield.png';
import SandclockSvg from 'public/images/svg_icons/sandclock.svg';
import MapPinSvg from 'public/images/svg_icons/map-pin.svg';
import Badge1Image from 'public/images/experience/badge_1.png';
import Badge2Image from 'public/images/experience/badge_2.png';
import Badge3Image from 'public/images/experience/badge_3.png';
import Badge4Image from 'public/images/experience/badge_4.png';
import Badge5Image from 'public/images/experience/badge_5.png';
import Badge6Image from 'public/images/experience/badge_6.png';
import Link from 'next/link';
import {
  EnumOptionCancelType,
  EnumTransportItemKind,
  EnumOptionKind,
  Option,
  Supplier,
  AvailableDate,
  ItineraryDay as TItineraryDay,
  OptionPage,
  Image,
  TransportItem,
  Timeslot,
  ItineraryDay,
  OrderItemAddon,
  AccommodationRoom,
  EnumOptionAccommodationSaleType,
  OptionLocation,
  City,
  Collection,
  FaqItem,
  OrderItemAccommodationRoom,
  TMoney,
  User
} from 'src/types';
import pluralize from 'pluralize';
import ReadMore from 'src/components/ReadMore';
import classNames from 'classnames';
import ApiClient from 'src/lib/api_client';
import OptionMedia from 'src/components/OptionMedia';
import { useRouter } from 'next/router';
import useGiftExperienceCode from 'src/hooks/useGiftExperienceCode';
import BookingForm, {
  BookingFormValues,
  ValidationSchema as BookingFormValidationSchema,
} from 'src/components/BookingForm';
import WaitListForm from 'src/components/WaitlistForm';
import RevalidateSettings from 'src/lib/revalidate_settings';
import UserFaqSection from 'src/components/UserFaqSection';
import ReviewSectionV2 from 'src/components/ReviewSectionV2';
import useInitialData from 'src/hooks/useInitialData';
import { getEleOffset, scrollToEle, isStayOrFlexible, getTotalQty, displaySupplierResponseTime } from 'src/helpers';
import ScrollableCollectionOptions from 'src/components/ScrollableCollectionOptions';
import { Formik, useFormikContext } from 'formik';
import OptionDatepicker, { OptionAvailabilityChecker } from 'src/components/OptionDatepicker';
import qs from 'qs';
import { ValidationError } from 'yup';
import OptionAccommodationRoomCarousel from 'src/components/OptionAccommodationRoomCarousel';
import flatpickr from 'flatpickr';
import { ControlHeaderPageNavContext } from 'src/contexts/ControlHeaderPageNavProvider';
import dayjs from 'dayjs';
import useCart from 'src/hooks/useCart';
import Head from 'next/head';
import { resolvePrice } from 'src/lib/price_resolution';
import WithDefaultJsonLd from 'src/components/WithDefaultJsonLd';
import useCurrentCurrency from 'src/hooks/useCurrentCurrency';
import Loading from 'src/components/Loading';
import AdminBar from 'src/components/AdminBar';
import useSearchBarState from 'src/hooks/useSearchBarState';
import notify from 'src/lib/social_event_notify';
import useCurrentUser from 'src/hooks/useCurrentUser';
import ImageWithFallback from 'src/components/ImageWithFallback';
import SupplierDefaultImg from 'public/images/supplier_default.png';
import { Modal, Pagination } from 'react-bootstrap';
import Money from 'src/components/Money';
import BookingTimeslotSelectorV2 from 'src/components/BookingTimeslotSelectorV2';
import { uniq, uniqBy, sortBy, flatten, min } from 'lodash-es';
import useBreakpoints from 'src/hooks/useBreakpoints';
import useIsMounted from 'src/hooks/useIsMounted';
import useEnhanceApiClient from 'src/hooks/useEnhanceApiClient';
import ModalAccommodationRoomProvider from 'src/contexts/ModalAccommodationRoomProvider';
import Homework from 'src/components/Homework';
import itinerarySectionStyle from 'src/styles/modules/components/option_itinerary_section.module.scss';
import TransportItemMap from 'src/components/option/TransportItemMap';
import AccordionFaq from 'src/components/AccordionFaq';
import { Overlay, OverlayTrigger } from 'react-bootstrap';
import styles from 'src/styles/modules/pages/experience.module.scss';
import useFacebookPixelEvent from 'src/hooks/useFacebookPixelEvent';
import ModalTransportItems from 'src/components/option/ModalTransportItems';
import useOptionAvailabilitySlotFetcher from 'src/hooks/useOptionAvailabilitySlotFetcher';
import sliderStyles from 'src/styles/modules/components/option_accommodation_room_carousel.module.scss';
import Slider from 'react-slick';
import { HiOutlineArrowNarrowLeft, HiOutlineArrowNarrowRight } from 'react-icons/hi';
import { StaticImageData } from 'next/image';
import Banner from 'src/components/Banner';
import Illu2 from 'public/images/gift_card/illu_2.png';
import Illu3 from 'public/images/gift_card/illu_3.png';
import useQuery from 'src/hooks/useQuery';
import { nanoid } from 'nanoid';
import useModalLogin from 'src/hooks/useModalLogin';
import humps from 'humps';
import useCurrencyExchange from 'src/hooks/useCurrencyExchange';

const AVAILABILITY_PER_PAGE = 200;

type BasicInfoProps = {
  option: Option;
  supplier: Supplier;
  city?: City;
  transportItems: TransportItem[];
  isGiftExperience?: boolean;
  setShowReviewModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const GIFT_EXPERIENCE_FAQS: FaqItem[] = [
  {
    id: nanoid(),
    question: 'Can the recipient select their own date for the experience?',
    answer: 'Yes, they can! Recipients have the flexibility to choose any date for their experience within a year of receiving the gift. It’s completely stress-free.',
  },
  {
    id: nanoid(),
    question: 'When will the Gift be sent?',
    answer: 'You can select the date the gift is sent during checkout. The gift will be emailed to the recipient at 8am on the chosen date. The one-year validity period starts from this date.',
  },
  {
    id: nanoid(),
    question: 'How do I know when the Gift has been sent?',
    answer: 'You’ll be copied on the gift email sent to the recipient, so you’ll know exactly when it’s been delivered.',
  },
  {
    id: nanoid(),
    question: 'Can Gift Experiences be exchanged for cash?',
    answer: 'Gift cards are all about creating fun and meaningful memories! They cannot be exchanged for cash. But if your recipient prefers another experience, we can swap out their experience for a gift card.',
  },
  {
    id: nanoid(),
    question: 'What if my recipient doesn’t like the Gift?',
    answer: 'No worries! They can contact us, and we’ll exchange it for a gift card of the same value. The gift card can be used for any of the 2,500+ experiences on Seek Sophie.',
  },
  {
    id: nanoid(),
    question: 'How does the recipient redeem their gift?',
    answer: 'It’s super simple! The recipient just needs to click the link in the gift email. This will take them to Seek Sophie, where they can choose their preferred date for the experience.',
  }
]

const DURATION_HOURS: Record<string, string> = {
  '0.25': '15 mins',
  '0.5': '30 mins',
  '0.75': '45 mins',
};

const BADGE_ITEMS: { [k: string]: { name: string; image: StaticImageData; defaultDescription: string; } } = {
  [badgeKinds.wildlifeSafe]: {
    name: 'Wildlife Protector',
    image: Badge1Image,
    defaultDescription:
      "Your host prioritises respectful wildlife viewing, maintaining safe distances and avoiding any disturbance to animals or their habitats. You'll observe animals in their natural state without interfering with their behaviours.",
  },
  [badgeKinds.litterFreeLeaveNoTrace]: {
    name: 'Leave No Trace Steward',
    image: Badge2Image,
    defaultDescription:
      'Your host adheres to Leave No Trace principles, ensuring minimal impact on the environment. They pack out their trash, avoid disturbing natural features, and refrain from taking souvenirs from nature.',
  },
  [badgeKinds.refusePlastics]: {
    name: 'Plastic Waste Warrior',
    image: Badge3Image,
    defaultDescription:
      "Your host is committed to reducing plastic waste wherever possible. They prioritise reusable alternatives, recycle diligently, and dispose of plastic responsibly. They're doing their best to protect our planet, even in areas where going completely plastic-free is a challenge.",
  },
  [badgeKinds.lowCarbon]: {
    name: 'Low Carbon Hero',
    image: Badge4Image,
    defaultDescription:
      'Your host provides a low-carbon travel experience by minimizing greenhouse gas emissions where possible in the context of the experience. This might involve using renewable energy, offering low-emission transportation like biking or walking, sourcing food locally, and choosing eco-friendly practices and materials.',
  },
  [badgeKinds.womenOwned]: {
    name: 'Women-led Wonder',
    image: Badge5Image,
    defaultDescription:
      "Your experience is offered by a business founded by women who are passionate about gender equality and contribute to a more equitable society.  While your host may not always be a woman, you're supporting a company committed to empowering women in their community.",
  },
  [badgeKinds.communityBased]: {
    name: 'Community Champion',
    image: Badge6Image,
    defaultDescription:
      'Our host is passionate about their community and uses tourism to create positive change. They provide jobs and training for locals, source goods nearby, and support projects that benefit the region.',
  },
};

const BasicInfo: React.FC<BasicInfoProps> = ({
  option,
  city,
  supplier,
  transportItems,
  isGiftExperience,
  setShowReviewModal,
}) => {
  const locationRef = useRef<HTMLSpanElement>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [showLocationTooltip, setShowLocationTooltip] = useState(false);
  const bookingTypeRef = useRef<HTMLSpanElement>(null);
  const overlayBookingTypeRef = useRef<HTMLDivElement | null>(null);
  const [showBookingTypeTooltip, setShowBookingTypeTooltip] = useState(false);
  const duration = useMemo(
    () =>
      option.kind === EnumOptionKind.shortAdventure
        ? option.durationHours
          ? option.durationHours >= 1
            ? pluralize('hour', option.durationHours, true)
            : DURATION_HOURS[String(option.durationHours)]
          : ''
        : isStayOrFlexible(option)
        ? 'Flexible'
        : option.durationDays || option.durationNights
        ? `${
            option.durationDays && option.durationDays >= 1
              ? pluralize('day', option.durationDays, true)
              : 'Less than 1 day'
          } ${option.durationNights ? pluralize('night', option.durationNights, true) : ''}`
        : null,
    [option.id]
  );
  const locations = ((option.locations || []) as (OptionLocation | City)[]).concat(
    option.otherCities || []
  );

  return (
    <>
      <div className="tw-mx-auto tw-max-w-xs md:tw-max-w-xl tw-px-2.5 md:tw-px-4">
        <h1 className="tw-font-semibold tw-font-styleSans tw-text-3xl lg:tw-text-6xl tw-mb-4 lg:tw-mb-8 tw-leading-none tw-text-center lg:tw-leading-none">
          {isGiftExperience ? '[GIFT] ' : ''}
          {option.name}
        </h1>

        {option.subtitle ? (
          <div className="tw-mb-8 tw-font-semibold tw-font-styleSans tw-text-lg md:tw-text-xl tw-text-center">
            {option.subtitle}
          </div>
        ) : null}

        {supplier.ratedReviewsCount > 0 && (
          <div className="tw-flex tw-justify-center tw-items-center tw-w-full tw-font-semibold tw-font-styleSans tw-mb-8">
            <div
              className="tw-cursor-pointer tw-flex tw-items-center"
              onClick={() => setShowReviewModal(true)}
            >
              <span className="tw-text-sm lg:tw-text-base tw-text-brand-navy tw-mr-1">
                {supplier.reviewRating}
              </span>
              <BsStarFill size={18} className="tw-text-orange tw-align-top tw-ml-0.5 tw-mb-1" />
            </div>
            <span className="tw-mx-2">|</span>
            <span
              className="tw-text-sm lg:tw-text-base tw-underline tw-cursor-pointer"
              onClick={() => setShowReviewModal(true)}
            >
              {pluralize('Review', supplier.ratedReviewsCount, true)}
            </span>
          </div>
        )}
      </div>

      <div className="tw-border tw-rounded-lg tw-border-grey-lighter tw-py-6 tw-px-4 md:tw-px-12 tw-flex lg:tw-justify-center tw-items-center tw-flex-wrap md:tw-flex-nowrap">
        {city ? (
          <div className="tw-w-1/2 tw-mb-6 md:tw-mb-0 md:tw-w-auto tw-flex-shrink-0 tw-flex tw-items-center">
            <ImageWithFallback src={PinPointIcon} alt="" className="tw-h-6 tw-w-6" />
            <div className="tw-ml-4">
              <div className="tw-text-sm tw-uppercase tw-font-semibold">Location</div>
              <div className="tw-text-sm md:tw-text-base">
                {city.name}{' '}
                {locations.length > 0 ? (
                  <div className="tw-inline">
                    <span
                      className="tw-underline tw-cursor-pointer"
                      onMouseLeave={() => setShowLocationTooltip(false)}
                      onMouseEnter={() => {
                        setShowLocationTooltip(true);
                        setShowBookingTypeTooltip(false);
                      }}
                      ref={locationRef}
                    >
                      +{locations.length}
                    </span>
                    <Overlay show={showLocationTooltip} placement="top" target={locationRef}>
                      {({ placement, arrowProps, show: _show, popper, ...props }) => (
                        <div
                          {...props}
                          ref={(e) => {
                            props.ref(e);
                            overlayRef.current = e;
                          }}
                          style={{
                            ...props.style,
                            width: '320px',
                          }}
                          className="tw-pb-1 tw-z-100"
                          onMouseEnter={() => {
                            setShowLocationTooltip(true);
                            setShowBookingTypeTooltip(false);
                          }}
                          onMouseLeave={() => setShowLocationTooltip(false)}
                        >
                          <div className="tw-bg-brand-yellow tw-p-8 tw-rounded-lg tw-shadow-lg">
                            {locations.map((o, index) => (
                              <span key={o.id}>
                                {index > 0 ? ', ' : ''}
                                <Link legacyBehavior prefetch={false}                                   href={
                                    'collectionSlugCode' in o
                                      ? o.collectionSlugCode
                                        ? AppRoutes.collectionRoute({
                                            id: o.collectionSlugCode,
                                          }).toPath()
                                        : '#'
                                      : 'slug' in o
                                      ? AppRoutes.cityRoute({ id: o.slug }).toPath()
                                      : '#'
                                  }
                                >
                                  <a className="tw-text-brand-navy tw-underline">{o.name}</a>
                                </Link>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </Overlay>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        <div className="tw-mx-6 tw-border-l tw-border-grey-lighter tw-h-10 tw-hidden md:tw-block"></div>

        <div className="tw-w-1/2 tw-mb-6 md:tw-mb-0 md:tw-w-auto tw-flex-shrink-0 tw-flex tw-items-center">
          <ImageWithFallback src={ClockIcon} alt="" className="tw-h-6 tw-w-6" />
          <div className="tw-ml-4">
            <div className="tw-text-sm tw-uppercase tw-font-semibold">DURATION</div>

            <div className="tw-text-sm md:tw-text-base">{duration}</div>
          </div>
        </div>

        <div className="tw-mx-6 tw-border-l tw-border-grey-lighter tw-h-10 tw-hidden md:tw-block"></div>

        <div className="tw-w-1/2 md:tw-w-auto tw-flex-shrink-0 tw-flex tw-items-center">
          <ImageWithFallback src={TicketIcon} alt="" className="tw-h-6 tw-w-6" />
          <div className="tw-ml-4">
            <div className="tw-text-sm tw-uppercase tw-font-semibold">Booking type</div>
            <div className="tw-text-sm md:tw-text-base tw-underline tw-cursor-pointer">
              <div className="tw-inline">
                <span
                  className="tw-underline tw-cursor-pointer"
                  onMouseLeave={() => setShowBookingTypeTooltip(false)}
                  onMouseEnter={() => {
                    setShowBookingTypeTooltip(true);
                    setShowLocationTooltip(false);
                  }}
                  ref={bookingTypeRef}
                >
                  {option.isPrivateTrip ? 'Private trip' : 'Join-in trip'}
                </span>
                <Overlay show={showBookingTypeTooltip} placement="top" target={bookingTypeRef}>
                  {({ placement, arrowProps, show: _show, popper, ...props }) => (
                    <div
                      {...props}
                      ref={(e) => {
                        props.ref(e);
                        overlayBookingTypeRef.current = e;
                      }}
                      style={{
                        ...props.style,
                        width: '320px',
                      }}
                      className="tw-pb-1 tw-z-100"
                      onMouseLeave={() => setShowBookingTypeTooltip(false)}
                      onMouseEnter={() => {
                        setShowBookingTypeTooltip(true);
                        setShowLocationTooltip(false);
                      }}
                    >
                      <div className="tw-bg-brand-yellow tw-p-8 tw-rounded-lg tw-shadow-lg">
                        {option.isPrivateTrip
                          ? 'Ditch the crowds and awkward small talk! This is a comfy private trip just for you and your favourite people. You can even customise the itinerary!'
                          : 'Connect with like-minded people on a group journey, and make memories together!'}
                      </div>
                    </div>
                  )}
                </Overlay>
              </div>
            </div>
          </div>
        </div>

        {transportItems.length > 0 && transportItems.every((o) => o.kind === 'meetup') ? (
          <>
            <div className="tw-mx-6 tw-border-l tw-border-grey-lighter tw-h-10 tw-hidden md:tw-block"></div>
            <div className="tw-w-1/2 md:tw-w-auto tw-flex-shrink-0 tw-flex tw-items-center">
              <ImageWithFallback src={FlagIcon} alt="" className="tw-h-6 tw-w-6" />
              <div className="tw-ml-4">
                <div className="tw-text-sm tw-uppercase tw-font-semibold">MEET UP</div>
                <div className="tw-text-sm md:tw-text-base">At location</div>
              </div>
            </div>
          </>
        ) : null}

        {transportItems.length > 0 && transportItems.some((o) => o.kind !== 'meetup') ? (
          <>
            <div className="tw-mx-6 tw-border-l tw-border-grey-lighter tw-h-10 tw-hidden md:tw-block"></div>
            <div className="tw-w-1/2 md:tw-w-auto tw-flex-shrink-0 tw-flex tw-items-center">
              <ImageWithFallback src={CarIcon} alt="" className="tw-h-6 tw-w-6" />
              <div className="tw-ml-4">
                <div className="tw-text-sm tw-uppercase tw-font-semibold">MEET UP</div>
                <div className="tw-text-sm md:tw-text-base">Free pick-up</div>
              </div>
            </div>
          </>
        ) : null}

        <div className="tw-hidden xl:tw-flex">
          <div className="tw-mx-6 tw-border-l tw-border-grey-lighter tw-h-10 tw-hidden md:tw-block"></div>
          <div className="tw-w-1/2 md:tw-w-auto tw-flex-shrink-0 tw-flex tw-items-center">
            <ImageWithFallback src={UserIcon} alt="" className="tw-h-6 tw-w-6" />
            <div className="tw-ml-4">
              <div className="tw-text-sm tw-uppercase tw-font-semibold">Group size</div>
              <div className="tw-text-sm md:tw-text-base">
                Up to {pluralize('guest', option.maxQuantity, true)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

type StartEndBlockProps = {
  option: Option;
  transportItems: TransportItem[];
  timeslots: Timeslot[];
};

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const humanizeRecurrenceType = (kind: string) => {
  return kind === 'weekly'
    ? 'Every'
    : kind === 'every_2_weeks'
    ? 'Every 2 weeks on'
    : kind === 'every_3_weeks'
    ? 'Every 3 weeks on'
    : kind === 'every_4_weeks'
    ? 'Every 4 weeks on'
    : '';
};

const StartEndBlock: React.FC<StartEndBlockProps> = ({ option, transportItems, timeslots }) => {
  const [showModal, setShowModal] = useState(false);
  const hasMoreThanOneTimeslotKind = uniq(timeslots.map((o) => o.kind)).length > 1;
  const groupedTimeslots = hasMoreThanOneTimeslotKind
    ? {}
    : timeslots
        .filter((o) => o.kind !== 'one_off')
        .reduce((memo, timeslot) => {
          const memoKeys =
            !timeslot.weekdays || timeslot.weekdays.length === 0
              ? [0, 1, 2, 3, 4, 5, 6]
              : timeslot.weekdays.map((wday) => Number(wday));
          memoKeys.map((memoKey) => {
            memo[memoKey] ||= [];
            const isNewTime = !memo[memoKey].some(
              (o) =>
                `${dayjs(o.startTime).format(UITimeFormat)}_${dayjs(o.endTime).format(
                  UITimeFormat
                )}` ===
                `${dayjs(timeslot.startTime).format(UITimeFormat)}_${dayjs(timeslot.endTime).format(
                  UITimeFormat
                )}`
            );
            if (isNewTime) {
              memo[memoKey].push(timeslot);
            }
          });
          return memo;
        }, {} as { [key: string]: Timeslot[] });
  const firstTimeslot = timeslots[0];

  const sortedDailyTimeslots =
    firstTimeslot?.kind === 'daily'
      ? sortBy(
          uniqBy(flatten(Object.values(groupedTimeslots)), (o) => {
            return `${dayjs(o.startTime).format(UITimeFormat)}_${dayjs(o.endTime).format(
              UITimeFormat
            )}`;
          }),
          [(o) => (o.interval ? 1 : 0), (o) => o.startTime]
        )
      : [];

  const maxNumLines = 6;
  const sortedTimeslotKeys = sortBy(Object.keys(groupedTimeslots), (v) =>
    Number(v) === 0 ? 8 : Number(v)
  );
  const canShowAdventureStartEnd =
    option.kind === EnumOptionKind.adventure && transportItems.length > 0;
  let lastPreShownDay: number | undefined;
  let lastDayNumSlot: number | undefined;
  sortedTimeslotKeys.reduce((memo, k) => {
    const num = memo + groupedTimeslots[k].length + 1;
    if (num >= maxNumLines && !lastPreShownDay) {
      lastPreShownDay = Number(k);
      lastDayNumSlot = maxNumLines - memo;
    }
    return num;
  }, 0);
  const canShowSeeMore =
    (firstTimeslot?.kind === 'daily' && sortedDailyTimeslots.length > 5) ||
    (firstTimeslot?.kind !== 'daily' &&
      typeof lastPreShownDay === 'number' &&
      lastDayNumSlot &&
      (groupedTimeslots[lastPreShownDay].length > lastDayNumSlot ||
        sortedTimeslotKeys.some((v) => lastPreShownDay && (Number(v) || 8) > lastPreShownDay))) ||
    (canShowAdventureStartEnd && transportItems.length > 3);

  return (
    <>
      {option.kind === EnumOptionKind.flexibleAdventure ? (
        <div className="tw-text-brand-navy">
          You can choose any start time. Just let your host know after booking.
        </div>
      ) : option.kind === EnumOptionKind.stay ? (
        <ul className="tw-pl-5">
          <li className="tw-mb-2">
            <div className="tw-text-brand-navy">Checkin time:</div>
            <div className="tw-text-brand-navy">After 02:00 PM</div>
          </li>
          <li>
            <div className="tw-text-brand-navy">Checkout time:</div>
            <div className="tw-text-brand-navy">Before 12:00 PM</div>
          </li>
        </ul>
      ) : canShowAdventureStartEnd ? (
        <div>
          {transportItems.length > 1 ? (
            <>
              <ul className="tw-pl-5">
                {transportItems.slice(0, 3).map((transportItem, index) => (
                  <li key={transportItem.id} className={index > 0 ? 'tw-mt-3' : ''}>
                    <div className="tw-text-brand-navy">
                      {transportItem.kind === EnumTransportItemKind.meetup
                        ? 'If Meet-up at '
                        : 'If Pick-up from '}
                      {transportItem.startPointDescription}
                    </div>
                    {transportItem?.timeFlexible ? (
                      <div className="tw-text-brand-navy">
                        You can choose any start time. Just let your host know after booking.
                      </div>
                    ) : null}
                    {transportItem?.transportTimes?.length > 0
                      ? transportItem?.transportTimes.map((transportTime, index) => (
                          <div
                            className={`tw-text-brand-navy ${index > 0 ? 'tw-mt-1' : ''}`}
                            key={transportTime.id}
                          >
                            {dayjs.utc(transportTime.startTime).format(UITimeFormat)} on Day 1 -{' '}
                            {dayjs.utc(transportTime.endTime).format(UITimeFormat)} on Day{' '}
                            {option.durationDays || 2}
                          </div>
                        ))
                      : null}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div>
              {transportItems[0]?.timeFlexible ? (
                <div className="tw-text-brand-navy">
                  You can choose any start time. Just let your host know after booking.
                </div>
              ) : null}
              {transportItems[0]?.transportTimes?.length > 0
                ? transportItems[0]?.transportTimes.map((transportTime, index) => (
                    <div
                      className={`tw-text-brand-navy ${index > 0 ? 'tw-mt-1' : ''}`}
                      key={transportTime.id}
                    >
                      {dayjs.utc(transportTime.startTime).format(UITimeFormat)} on Day 1 -{' '}
                      {dayjs.utc(transportTime.endTime).format(UITimeFormat)} on Day{' '}
                      {option.durationDays || 2}
                    </div>
                  ))
                : null}
            </div>
          )}
        </div>
      ) : option.kind === EnumOptionKind.shortAdventure ? (
        <>
          {hasMoreThanOneTimeslotKind ? (
            <div className="tw-text-brand-navy">Select your preferred date to see timings.</div>
          ) : Object.keys(groupedTimeslots).length > 0 ? (
            <>
              {firstTimeslot.kind === 'daily' ? (
                <div>
                  <div className="tw-text-brand-navy">Daily</div>
                  {sortedDailyTimeslots.slice(0, 5).map((timeslot) => (
                    <div key={timeslot.id} className="tw-text-brand-navy">
                      {timeslot.interval ? (
                        <div>Guests can choose any start time between</div>
                      ) : null}
                      {dayjs.utc(timeslot.startTime).format(UITimeFormat)} -{' '}
                      {dayjs.utc(timeslot.endTime).format(UITimeFormat)}
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <ul className="tw-pl-5">
                    {sortedTimeslotKeys
                      .filter((k) => (lastPreShownDay ? (Number(k) || 8) <= lastPreShownDay : true))
                      .map((weekday, index) => (
                        <li key={weekday} className={index > 0 ? 'tw-mt-3' : ''}>
                          <div className="tw-text-brand-navy">
                            {humanizeRecurrenceType(firstTimeslot.kind)} {WEEKDAYS[Number(weekday)]}
                          </div>
                          {sortBy(
                            groupedTimeslots[weekday].slice(
                              0,
                              Number(weekday) === lastPreShownDay ? lastDayNumSlot : undefined
                            ),
                            [(o) => (o.interval ? 1 : 0), (o) => o.startTime]
                          ).map((timeslot) => (
                            <div key={timeslot.id} className="tw-text-brand-navy">
                              {timeslot.interval ? (
                                <div>Guests can choose any start time between</div>
                              ) : null}
                              {dayjs.utc(timeslot.startTime).format(UITimeFormat)} -{' '}
                              {dayjs.utc(timeslot.endTime).format(UITimeFormat)}
                            </div>
                          ))}
                        </li>
                      ))}
                  </ul>
                </>
              )}
            </>
          ) : (
            <div className="tw-text-brand-navy">Select your preferred date to see timings.</div>
          )}
        </>
      ) : null}

      {canShowSeeMore ? (
        <div
          className="tw-font-semibold tw-underline tw-cursor-pointer tw-mt-3 tw-text-brand-navy"
          onClick={() => setShowModal(true)}
        >
          See More
        </div>
      ) : null}

      {canShowSeeMore ? (
        <Modal show={showModal} className="modal-styled">
          <Modal.Header>
            <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
              <div className="tw-text-xl md:tw-text-4xl tw-font-semibold">Start & Ends</div>
              <FiX size={24} className="tw-cursor-pointer" onClick={() => setShowModal(false)} />
            </div>
          </Modal.Header>
          <Modal.Body>
            {canShowAdventureStartEnd ? (
              <>
                <ul className="tw-pl-5">
                  {transportItems.map((transportItem, index) => (
                    <li key={transportItem.id} className={index > 0 ? 'tw-mt-3' : ''}>
                      <div className="tw-text-brand-navy">
                        {transportItem.kind === EnumTransportItemKind.meetup
                          ? 'If Meet-up at '
                          : 'If Pick-up from '}
                        {transportItem.startPointDescription}
                      </div>
                      {transportItem?.timeFlexible ? (
                        <div className="tw-text-brand-navy">
                          You can choose any start time. Just let your host know after booking.
                        </div>
                      ) : null}
                      {transportItem?.transportTimes?.length > 0
                        ? transportItem?.transportTimes.map((transportTime, index) => (
                            <div
                              className={`tw-text-brand-navy ${index > 0 ? 'tw-mt-1' : ''}`}
                              key={transportTime.id}
                            >
                              {dayjs.utc(transportTime.startTime).format(UITimeFormat)} on Day 1 -{' '}
                              {dayjs.utc(transportTime.endTime).format(UITimeFormat)} on Day{' '}
                              {option.durationDays || 2}
                            </div>
                          ))
                        : null}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                {firstTimeslot.kind === 'daily' ? (
                  <div>
                    <div className="tw-text-brand-navy tw-mb-2">Daily</div>
                    {sortedDailyTimeslots.map((timeslot) => (
                      <div key={timeslot.id} className="tw-text-brand-navy">
                        {timeslot.interval ? (
                          <div>Guests can choose any start time between</div>
                        ) : null}
                        {dayjs.utc(timeslot.startTime).format(UITimeFormat)} -{' '}
                        {dayjs.utc(timeslot.endTime).format(UITimeFormat)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <ul className="tw-pl-5">
                      {sortedTimeslotKeys.map((weekday, index) => (
                        <li key={weekday} className={index > 0 ? 'tw-mt-3' : ''}>
                          <div className="tw-text-brand-navy">
                            {humanizeRecurrenceType(firstTimeslot.kind)} {WEEKDAYS[Number(weekday)]}
                          </div>
                          {sortBy(groupedTimeslots[weekday], [
                            (o) => (o.interval ? 1 : 0),
                            (o) => o.startTime,
                          ]).map((timeslot) => (
                            <div key={timeslot.id} className="tw-text-brand-navy">
                              {timeslot.interval ? (
                                <div>Guests can choose any start time between</div>
                              ) : null}
                              {dayjs.utc(timeslot.startTime).format(UITimeFormat)} -{' '}
                              {dayjs.utc(timeslot.endTime).format(UITimeFormat)}
                            </div>
                          ))}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
          </Modal.Body>
        </Modal>
      ) : null}
    </>
  );
};

type CancellationProps = {
  option: Option;
};

const CancellationBlock: React.FC<CancellationProps> = ({ option }) => {
  const [showModal, setShowModal] = useState(false);
  const policies = useMemo(() => {
    return [
      {
        name: 'Cancellation',
        content:
          option.cancelType === EnumOptionCancelType.noCancellation || !option.cancelType
            ? `To secure your spot, your host will turn away other guests. Because of this, cancellations aren't allowed.`
            : `Cancel ${
                option.cancellationDueDays === 1
                  ? '24 hours'
                  : pluralize('day', option.cancellationDueDays || 7, true)
              } before activity for a full refund`,
      },
      !option.isPrivateTrip && option.minQuantity && option.minQuantity > 1
        ? {
            name: 'Minimum Participants',
            content: `If there are fewer than ${option.minQuantity} participants, the host may reschedule or refund your booking.`,
          }
        : null,
      option.reschedulePolicy
        ? {
            name: 'Reschedule Policy',
            content: option.reschedulePolicy,
          }
        : null,
      option.wetWeatherPolicy
        ? {
            name: 'Bad Weather Policy',
            content: option.wetWeatherPolicy,
          }
        : null,
      option.extenuatingPolicy
        ? {
            name: 'Extenuating Circumstances Policy',
            content: option.extenuatingPolicy,
          }
        : null,
    ].filter((v) => v) as { name: string; content: string }[];
  }, [option.id]);

  return (
    <>
      <ul className="tw-pl-5">
        {policies.slice(0, 2).map((policy, index) => (
          <li key={index} className={index > 0 ? 'tw-mt-3' : ''}>
            <div className="tw-text-brand-navy tw-font-semibold">{policy.name}</div>
            <div className="tw-text-brand-navy">{policy.content}</div>
          </li>
        ))}
      </ul>

      {policies.length > 2 ? (
        <>
          <div
            className="tw-font-semibold tw-text-grey tw-underline tw-cursor-pointer"
            onClick={() => setShowModal(true)}
          >
            See More
          </div>
          <Modal show={showModal} className="modal-styled">
            <Modal.Header>
              <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
                <div className="tw-text-xl md:tw-text-4xl tw-font-semibold">
                  Cancellation & Other Policies
                </div>
                <FiX size={24} className="tw-cursor-pointer" onClick={() => setShowModal(false)} />
              </div>
            </Modal.Header>
            <Modal.Body>
              {policies.map((policy, index) => (
                <div key={index} className={index > 0 ? 'tw-mt-6' : ''}>
                  <div className="tw-text-black tw-text-lg tw-font-semibold">{policy.name}</div>
                  <div className="tw-text-brand-navy">{policy.content}</div>
                </div>
              ))}
            </Modal.Body>
          </Modal>
        </>
      ) : null}
    </>
  );
};

type ItinerarySectionProps = {
  option: Option;
  itineraryDays: ItineraryDay[];
};
const ItinerarySection: React.FC<ItinerarySectionProps> = ({ option, itineraryDays }) => {
  const [showItineraryModal, setShowItineraryModal] = useState<boolean>(false);

  return (
    <>
      {itineraryDays.length > 0 && !isStayOrFlexible(option) ? (
        <>
          <div className="tw-my-12 lg:tw-my-16 tw-border-t tw-border-grey-lighter"></div>
          <div id="itinerary" className="tw-mt-8">
            <div className="tw-text-center md:tw-text-left tw-mx-auto tw-max-w-xs md:tw-max-w-none tw-px-2.5 md:tw-px-0 tw-text-3xl lg:tw-text-4xl tw-font-semibold tw-text-brand-navy tw-font-styleSans tw-mb-8 tw-leading-none lg:tw-leading-none">
              What You’ll be Doing
            </div>

            {option.kind === EnumOptionKind.shortAdventure ? (
              <div className="tw-font-semibold tw-font-styleSans tw-text-lg md:tw-text-xl">
                {itineraryDays.map((itineraryDay, index) => (
                  <span key={itineraryDay.id}>{itineraryDay.summary || itineraryDay.title}</span>
                ))}
              </div>
            ) : (
              <div className={itinerarySectionStyle.timeline}>
                {itineraryDays.map((itineraryDay, index) => (
                  <div
                    className={classNames(`${itinerarySectionStyle.item}`)}
                    key={itineraryDay.id}
                  >
                    <div
                      className={classNames('md:tw-ml-6', {
                        'tw-pt-6 tw-border-t tw-border-grey-lighter': index > 0,
                      })}
                      key={itineraryDay.id}
                    >
                      <div className="tw-font-semibold tw-text-lg md:tw-text-xl tw-font-styleSans tw-mb-4">
                        {`Day ${index + 1}`}:
                      </div>
                      {itineraryDay.summary || itineraryDay.title}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div
              className="tw-font-semibold tw-font-styleSans tw-lowercase tw-underline tw-cursor-pointer tw-mt-6 tw-text-center md:tw-text-left"
              onClick={() => setShowItineraryModal(true)}
            >
              Show Details
            </div>
          </div>

          <Modal
            show={showItineraryModal}
            className="modal-styled"
            onHide={() => {
              setShowItineraryModal(false);
            }}
          >
            <Modal.Header>
              <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
                <div className="tw-text-xl md:tw-text-2xl tw-font-semibold tw-font-styleSans tw-leading-none md:tw-leading-none">
                  What You’ll be Doing
                </div>
                <FiX
                  size={24}
                  className="tw-cursor-pointer"
                  onClick={() => setShowItineraryModal(false)}
                />
              </div>
            </Modal.Header>
            <Modal.Body>
              {itineraryDays.map((itinerary, index) => {
                const images = itinerary.images
                  ? itinerary.images
                  : itinerary.image
                  ? [itinerary.image]
                  : [];
                return (
                  <div key={itinerary.id}>
                    {index > 0 ? (
                      <div className="tw-border-t tw-border-grey-lighter tw-my-8"></div>
                    ) : null}

                    <div className="tw-mb-2">
                      <span className="tw-font-semibold tw-font-styleSans tw-text-lg md:tw-text-xl tw-leading-none md:tw-leading-none">
                        {option.kind === EnumOptionKind.shortAdventure
                          ? 'Itinerary'
                          : `Day ${index + 1}`}
                      </span>
                    </div>

                    <div className="tw-mb-6">{itinerary.summary}</div>

                    <ul className="tw-pl-5 tw-mb-6">
                      {itinerary.descriptionLines.map((text, index) => (
                        <li key={index} className={`${index > 0 ? 'tw-mt-2' : ''}`}>
                          {text}
                        </li>
                      ))}
                    </ul>

                    {images.length > 0 ? (
                      <div className="tw--mx-1 lg:tw--mx-2 tw-flex tw-flex-nowrap tw-overflow-x-auto custom-scrollbar">
                        {images.map((image) => (
                          <div
                            className={`${
                              images.length === 3
                                ? 'tw-w-1/3'
                                : images.length === 2
                                ? 'tw-w-1/2'
                                : images.length === 1
                                ? 'tw-w-full'
                                : ''
                            } tw-h-44 lg:tw-h-72 tw-px-1 lg:tw-px-2 tw-flex-shrink-0 tw-pb-2`}
                            key={image.id}
                            style={images.length > 3 ? { width: '30%' } : {}}
                          >
                            <div className="tw-relative tw-w-full tw-h-full">
                              <ImageWithFallback
                                record={image}
                                imgField="webpMdUrl"
                                alt=""
                                fill
                                className="tw-object-cover tw-w-full tw-rounded-lg tw-border-2 tw-border-brand-green tw-border-solid"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </Modal.Body>
          </Modal>
        </>
      ) : null}
    </>
  );
};

type OptionBadgesProps = {
  option: Option;
  transportItems: TransportItem[];
};
const OptionBadges: React.FC<OptionBadgesProps> = ({ option, transportItems }) => {
  const duration = useMemo(
    () =>
      option.kind === EnumOptionKind.shortAdventure
        ? option.durationHours
          ? option.durationHours >= 1
            ? pluralize('hour', option.durationHours, true)
            : DURATION_HOURS[String(option.durationHours)]
          : ''
        : isStayOrFlexible(option)
        ? 'Flexible'
        : option.durationDays || option.durationNights
        ? `${
            option.durationDays && option.durationDays >= 1
              ? pluralize('day', option.durationDays, true)
              : 'Less than 1 day'
          } ${option.durationNights ? pluralize('night', option.durationNights, true) : ''}`
        : null,
    [option.id]
  );

  return (
    <div className="tw-flex tw-flex-wrap">
      {duration ? (
        <div className="tw-text-sm tw-text-black tw-bg-grey-lightest tw-rounded-2xl tw-py-1 tw-px-2 tw-mr-2 md:tw-mr-6 tw-mb-2 lg:tw-mb-0">
          <SandclockSvg className="tw-mr-2 tw-w-5 tw-h-5" />
          <span>{duration}</span>
        </div>
      ) : null}

      <div className="tw-text-sm tw-text-black tw-bg-grey-lightest tw-rounded-2xl tw-py-1 tw-px-2 tw-mr-2 md:tw-mr-6 tw-mb-2 lg:tw-mb-0">
        <User1Svg className="tw-mr-2 tw-w-5 tw-h-5" />
        <span>{option.isPrivateTrip ? 'Private trip' : 'Join-in trip'}</span>
      </div>

      <div className="tw-text-sm tw-text-black tw-bg-grey-lightest tw-rounded-2xl tw-py-1 tw-px-2 tw-mr-2 md:tw-mr-6 tw-mb-2 lg:tw-mb-0">
        <span>Up to {pluralize('guest', option.maxQuantity, true)}</span>
      </div>

      {transportItems.some((o) => o.kind !== 'meetup') ? (
        <div className="tw-text-sm tw-text-black tw-bg-grey-lightest tw-rounded-2xl tw-py-1 tw-px-2 tw-mb-2 lg:tw-mb-0">
          <span>Free pick-up</span>
        </div>
      ) : null}
    </div>
  );
};

const PageNav: React.FC<{
  option: Option;
  hasReview: boolean;
  availableDates: AvailableDate[];
  accommodationRooms: AccommodationRoom[];
  isGiftExperience?: boolean;
  itineraryDays: TItineraryDay[];
  show: boolean;
  isPublished: boolean;
}> = ({
  show,
  option,
  accommodationRooms,
  availableDates,
  isGiftExperience,
  itineraryDays,
  hasReview,
  isPublished
}) => {
  const pageNavRef = useRef<HTMLDivElement | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>();
  const { showHeader } = useContext(ControlHeaderPageNavContext);
  const items = useMemo(
    () => [
      { name: 'Experience Highlights', target: '#highlight', canShow: true },
      {
        name: 'Itinerary',
        target: '#itinerary',
        canShow: !isStayOrFlexible(option) && itineraryDays.length > 0,
      },
      {
        name: 'Inclusions',
        target: '#inclusion',
        canShow: option.inclusions.length > 0 || option.exclusions.length > 0,
      },
      {
        name: 'Accommodation',
        target: '#accommodation',
        canShow: accommodationRooms && accommodationRooms.length > 0,
      },
      {
        name: 'Availability',
        target: '#availability',
        canShow: availableDates.length > 0 && !isGiftExperience && isPublished,
      },
      { name: 'More Info', target: '#location', canShow: true },
      { name: 'Host', target: '#host', canShow: true },
      { name: 'Reviews', target: '#review', canShow: hasReview },
    ],
    [option.id]
  );

  useEffect(() => {
    if (!pageNavRef.current) return;
    const pageNavEle = pageNavRef.current;
    const findActiveItem = () => {
      const scrollPos = window.scrollY;
      let nextCurrentIndex: number | undefined;
      let left = 0;

      items
        .filter((item) => item.canShow)
        .map((item, index) => {
          const sectionEle: HTMLElement | null = document.querySelector(item.target);
          if (!sectionEle) return;

          var positionTop = getEleOffset(sectionEle).top - pageNavEle.offsetHeight;

          if (positionTop <= scrollPos && positionTop + sectionEle.offsetHeight > scrollPos) {
            nextCurrentIndex = index;
            const links: HTMLElement[] = Array.from(pageNavEle.querySelectorAll('.page-nav__item'));
            const marginLeft = 20;
            left = [...Array(index)]
              .map((_, i) => i)
              .reduce((sum, i) => {
                sum = sum + links[i].offsetWidth + marginLeft;
                return sum;
              }, 0);
          }
        });

      setCurrentIndex(nextCurrentIndex);
      const pageNavListEle = pageNavEle.querySelector('.page-nav__list');
      if (
        typeof nextCurrentIndex === 'number' &&
        pageNavListEle &&
        pageNavListEle.scrollLeft !== left
      ) {
        pageNavListEle.scrollLeft = left;
      }
    };

    document.addEventListener('scroll', findActiveItem);

    return () => {
      document.removeEventListener('scroll', findActiveItem);
    };
  }, [option.id]);

  return (
    <div
      ref={pageNavRef}
      className={classNames(
        `page-nav tw-fixed tw-w-full tw-bg-brand-navy tw-py-5 tw-z-90 tw-h-auto ${
          showHeader ? styles.pageNav : 'tw-top-0'
        }`,
        { 'tw-block': show }
      )}
    >
      <div className="container">
        <ul className="page-nav__list">
          {items
            .filter((item) => item.canShow)
            .map((item, index) => (
              <li
                key={item.target}
                className="page-nav__item"
                onClick={scrollToEle(item.target, -(pageNavRef.current?.offsetHeight || 0))}
              >
                <span className={classNames('page-nav__link', { active: index === currentIndex })}>
                  {item.name}
                </span>
              </li>
            ))}
        </ul>
      </div>
    </div>
  );
};

const OptionShow: React.FC<
  InferGetStaticPropsType<typeof getStaticProps> & { addToCartError?: string }
> = ({
  option,
  supplier,
  collection,
  transportItems,
  itineraryDays,
  transports,
  addons,
  tags,
  accessibilityTagIds = [],
  optionReviewPagination,
  faqs,
  faqPagination,
  prices,
  accommodationRooms,
  addToCartError,
  availableDates: initialAvailableDates,
  availableDatePagination,
  timeslots,
  endPoints = [],
  giftCollection
}) => {
  const currencyExchange = useCurrencyExchange();
  const [currentUser] = useCurrentUser();
  const getSimilarOptions = useEnhanceApiClient(ApiClient.getSimilarOptions);
  const getOptions = useEnhanceApiClient(ApiClient.getOptions);
  const getAvailableDates = useEnhanceApiClient(ApiClient.getAvailableDates);
  const getDetailedTimeslots = useEnhanceApiClient(ApiClient.getDetailedTimeslots);
  const inclusionsExclusions = option.inclusions
    .map((v) => ({
      text: v,
      kind: 'inclusion',
    }))
    .concat(
      option.exclusions.map((v) => ({
        text: v,
        kind: 'exclusion',
      }))
    );
  const [showAllInclusion, setShowAllInclusion] = useState(
    option.inclusions.length > 4 ||
      option.exclusions.length > 0 ||
      transports.length > 0 ||
      addons.length > 0
      ? false
      : true
  );
  const senddFacebookPixelEvent = useFacebookPixelEvent();
  const router = useRouter();
  const [showPageNav, setShowPageNav] = useState(false);
  const [searchBarParams] = useSearchBarState();
  const [currentCart] = useCart();
  const orderItem = currentCart.orderItem;
  const [sectionDatepickerDate, setSectionDatepickerDate] = useState<string | undefined>();
  const [sectionDetailedTimeslotId, setSectionDetailedTimeslotId] = useState<number | undefined>();
  const stickPageNavPositionRef = useRef<HTMLDivElement>(null);
  const [similarOptions, setSimilarOptions] = useState<Option[]>();
  const [supplierOtherOptions, setSupplierOtherOptions] = useState<Option[]>();
  const [locationOptions, setLocationOptions] = useState<Option[]>();
  const { showHeader, setShowHeader } = useContext(ControlHeaderPageNavContext);
  const location = option.locations ? option.locations[0] : undefined;
  const [showLocationModal, setShowLocationModal] = useState(false);
  const { isScreenLg } = useBreakpoints();
  const isMounted = useIsMounted();
  const {
    availableDatesNoDurationConstraint,
    availableDates,
    availableDatesData,
    detailedTimeslots,
    detailedTimeslotsData,
    isLoadingAvailability,
  } = useOptionAvailabilitySlotFetcher(option, initialAvailableDates, availableDatePagination);

  const initialData = useInitialData();
  const city = initialData.cities.find((o) => o.id === option.cityId);
  const country = initialData.countries.find((o) => o.id === option.countryId);
  const metaTitle = option.name;
  const metaDesc = `${metaTitle} - ${supplier.strippedDescription}`;
  const { giftExperienceCode, isLoading: isLoadingGiftExperienceCode } = useGiftExperienceCode();
  const isGiftExperience = router.query.gift_experience === '1' && option.isGiftable;
  const currentCurrency = useCurrentCurrency();
  const sectionDatepickerRef = useRef<flatpickr.Instance>();
  const suitableTags = useMemo(
    () =>
      sortBy(
        tags.filter((tag) => accessibilityTagIds.includes(tag.id)),
        (tag) => accessibilityTagIds.findIndex((i) => i === tag.id)
      ),
    [option.id]
  );
  const optionBadges = option.useSupplierBadges ? supplier.badges : option.badges;
  const [showReviewModal, setShowReviewModal] = useState(false);
  const {
    values: bookingFormValues,
    setFieldValue,
    setValues,
  } = useFormikContext<BookingFormValues>();
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [showBookingBottomBar, setShowBookingBottomBar] = useState(false);
  const [timeslotDate, setTimeslotDate] = useState<string>();
  const [isOpenTimeslotSelector, setIsOpenTimeslotSelector] = useState(false);
  const availabilityChecker = new OptionAvailabilityChecker(
    option,
    availableDates,
    detailedTimeslots,
    bookingFormValues
  );
  const prevPosRef = useRef(0);
  const isPrivateAccess = option.isPrivateAccess && !!option.privateAccessCode &&
    option.privateAccessCode === String(router.query.pc) && !!option.privateAccessCodeExpiredAt &&
    dayjs(option.privateAccessCodeExpiredAt).isAfter(dayjs())
  const isPublished = option.isPublished || isPrivateAccess
  const createChatConversation = useEnhanceApiClient(ApiClient.createChatConversation);
  const [isCreatingChatConversation, setIsCreatingChatConversation] = useState(false)
  const [isTouched, setIsTouched] = useState(false)
  const { show: showModalLogin, setShow: setShowModalLogin } = useModalLogin()
  const [chatConversation, setChatConversation] = useState<{ id: number; chatInquiryId?: number; }>()
  const [showMessageHostModal, setShowMessageHostModal] = useState(false)

  const chatWithSupplier = () => {
    if (chatConversation) {
      setShowMessageHostModal(true)
    } else {
      setIsCreatingChatConversation(true)
      createChatConversation({ supplierActivityOptionId: option.id }).then((response) => {
        const [json] = response
        if (!('error' in json)) {
          setChatConversation({ id: json.id, chatInquiryId: json.chatInquiryId })
          setShowMessageHostModal(true)
        }
      }).finally(() => {
        setShowModalLogin(false)
        setIsCreatingChatConversation(false)
      })
    }
  }

  useEffect(() => {
    const callback = (event: CustomEvent<User>) => {
      if (!isTouched) return
      chatWithSupplier()
    }
    document.addEventListener('ss:logged_in', callback, { once: true })

    return () => {
      document.removeEventListener('ss:logged_in', callback)
    }
  }, [option.id, JSON.stringify(bookingFormValues), isTouched])

  useEffect(() => {
    if (!showModalLogin) {
      setIsTouched(false)
    }
  }, [showModalLogin])


  useEffect(() => {
    const toggleEle = () => {
      if (stickPageNavPositionRef.current) {
        const currentPos = window.scrollY;
        if (prevPosRef.current > currentPos) {
          setShowHeader(true);
          setShowBookingBottomBar(false);
          if (currentPos < getEleOffset(stickPageNavPositionRef.current).top) {
            setShowPageNav(false);
          }
        } else if (currentPos === 0) {
          setShowHeader(true);
          setShowBookingBottomBar(false);
        } else if (prevPosRef.current < currentPos) {
          if (currentPos >= getEleOffset(stickPageNavPositionRef.current).top) {
            setShowHeader(false);
            setShowPageNav(true);
            setShowBookingBottomBar(true);
          } else {
          }
        }
        prevPosRef.current = currentPos;
      }
    };

    let isFetching = false;
    const loadSimilarOptions = () => {
      if (isFetching) return;
      if (window.scrollY > Math.floor(document.body.clientHeight / 3)) {
        isFetching = true;
        getSimilarOptions({ id: option.id }).then((result) => {
          const [json] = result;
          if ('data' in json) {
            setSimilarOptions(json.data);
          } else {
            setSimilarOptions([]);
          }
        });

        getOptions({
          per_page: 9,
          q: {
            supplier_id_eq: supplier.id,
            discarded_at_null: 1,
            published_at_not_null: 1,
            id_not_eq: option.id,
            s: ['add_to_cart_count desc'],
          },
        }).then((result) => {
          const [json] = result;
          if ('data' in json) {
            setSupplierOtherOptions(json.data);
          } else {
            setSupplierOtherOptions([]);
          }
        });

        if (location) {
          getOptions({
            per_page: 9,
            q: {
              locations_id_eq: location.id,
              id_not_eq: option.id,
              discarded_at_null: 1,
              published_at_not_null: 1,
              s: ['add_to_cart_count desc'],
            },
          }).then((result) => {
            const [json] = result;
            if ('data' in json) {
              setLocationOptions(json.data);
            } else {
              setLocationOptions([]);
            }
          });
        }

        document.removeEventListener('scroll', loadSimilarOptions);
      }
    };

    document.addEventListener('scroll', toggleEle);
    document.addEventListener('scroll', loadSimilarOptions);

    return () => {
      document.removeEventListener('scroll', toggleEle);
      document.removeEventListener('scroll', loadSimilarOptions);
    };
  }, [option.id]);

  useEffect(() => {
    if (isLoadingGiftExperienceCode) return;
    if (giftExperienceCode?.code && giftExperienceCode?.optionId === option.id) {
      setValues({
        ...bookingFormValues,
        detailedTimeslotId: orderItem?.detailedTimeslotId || bookingFormValues.detailedTimeslotId,
        startDate: orderItem?.startDate || bookingFormValues.startDate,
        endDate: orderItem?.endDate || bookingFormValues.endDate,
        quantity: orderItem?.quantity || giftExperienceCode.quantity || 0,
        childQuantity: orderItem?.childQuantity || giftExperienceCode.childQuantity || 0,
        localQuantity: orderItem?.localQuantity || giftExperienceCode.localQuantity || 0,
        childLocalQuantity:
          orderItem?.childLocalQuantity || giftExperienceCode.childLocalQuantity || 0,
        transportId: orderItem?.transportId || giftExperienceCode.transportId,
        addons: orderItem?.orderItemAddons || giftExperienceCode.addons,
      });
    } else if (orderItem && orderItem.id && orderItem.supplierActivityOptionId === option.id) {
      setValues({
        ...bookingFormValues,
        quantity: orderItem.quantity,
        childQuantity: orderItem.childQuantity,
        localQuantity: orderItem.localQuantity,
        childLocalQuantity: orderItem.childLocalQuantity,
        hasLocal: orderItem.localQuantity > 0,
        orderItemAccommodationRooms: flatten(
          bookingFormValues.orderItemAccommodationRooms.map((o) => {
            const orderItemAccommodationRooms = (
              orderItem.orderItemAccommodationRooms || []
            ).filter((obj) => obj.accommodationRoomId === o.accommodationRoomId);

            return orderItemAccommodationRooms.length > 0
              ? orderItemAccommodationRooms.map((orderItemAccommodationRoom) => {
                  return {
                    ...o,
                    qty: orderItemAccommodationRoom.qty,
                    numOfPerson: orderItemAccommodationRoom.numOfPerson,
                    masterAmount: orderItemAccommodationRoom.masterAmount,
                    amount: orderItemAccommodationRoom.amount
                  };
                })
              : [o];
          })
        ),
        addons: orderItem.orderItemAddons || bookingFormValues.addons,
        transportId: orderItem.transportId,
        detailedTimeslotId: isGiftExperience ? null : orderItem.detailedTimeslotId,
        startDate: isGiftExperience ? null : orderItem.startDate,
        endDate: isGiftExperience ? null : isStayOrFlexible(option) ? orderItem.endDate : null,
        bookType: isGiftExperience ? 'instant' : option.bookType,
        giftExperience: isGiftExperience,
      });
    } else {
      let reqAddons: { id: number | string; qty: number | string }[] = [];
      let tmpAddons: OrderItemAddon[] = [];
      try {
        reqAddons = typeof router.query.addons === 'string' ? JSON.parse(router.query.addons) : [];
        tmpAddons = reqAddons
          .map((reqAddon) => {
            const addon = addons.find((o) => o.id === Number(reqAddon.id));
            return addon
              ? {
                  name: addon.name,
                  addonId: addon.id,
                  qty: Number(reqAddon.qty),
                  masterAmount: addon.masterAmount,
                  amount: addon.amount
                }
              : null;
          })
          .filter((v) => v) as OrderItemAddon[];
      } catch {}

      let reqAccommodationRooms: {
        accommodationRoomId: number;
        qty: number;
        amount: TMoney;
        masterAmount: TMoney;
        numOfPerson: number;
      }[] = []
      let tmpAccommodationRooms: OrderItemAccommodationRoom[] = bookingFormValues.orderItemAccommodationRooms

      try {
        reqAccommodationRooms = (typeof router.query.accommodation_rooms === 'string' ? humps.camelizeKeys(JSON.parse(router.query.accommodation_rooms)) : []) as {
          accommodationRoomId: number;
          qty: number;
          amount: TMoney;
          masterAmount: TMoney;
          numOfPerson: number;
        }[];
        tmpAccommodationRooms = bookingFormValues.orderItemAccommodationRooms
          .map((orderItemAccommodationRoom) => {
            const reqAccommodationRoom = reqAccommodationRooms.find((o) => o.accommodationRoomId === orderItemAccommodationRoom.accommodationRoomId);
            return reqAccommodationRoom
              ? {
                  ...orderItemAccommodationRoom,
                  qty: reqAccommodationRoom.qty,
                  numOfPerson: reqAccommodationRoom.numOfPerson,
                  masterAmount: reqAccommodationRoom.masterAmount,
                  amount: currencyExchange({ amount: reqAccommodationRoom.amount, toCurrency: currentCurrency }),
                }
              : orderItemAccommodationRoom;
          })
          .filter((v) => v) as OrderItemAccommodationRoom[]
      } catch {}

      setValues({
        ...bookingFormValues,
        orderItemAccommodationRooms: tmpAccommodationRooms,
        startDate: searchBarParams.dates[0] || (router.query.start_date ? String(router.query.start_date) : null),
        endDate: null,
        detailedTimeslotId: router.query.detailed_timeslot_id ? Number(router.query.detailed_timeslot_id) : null,
        addons: tmpAddons,
        transportId: router.query.transport_id ? Number(router.query.transport_id) : null,
        supplierActivityOptionId: option.id,
        hasLocal: false,
        quantity: isGiftExperience
          ? 0
          : typeof searchBarParams.numGuest === 'number'
          ? min([searchBarParams.numGuest, option.maxQuantity]) || 0
          : router.query.quantity
          ? Number(router.query.quantity)
          : 0,
        childQuantity: router.query.child_quantity ? Number(router.query.child_quantity) : bookingFormValues.childQuantity,
        childLocalQuantity: router.query.child_local_quantity ? Number(router.query.child_local_quantity) : bookingFormValues.childLocalQuantity,
        localQuantity: router.query.local_quantity ? Number(router.query.local_quantity) : bookingFormValues.localQuantity,
        giftExperience: isGiftExperience,
        bookType: isGiftExperience ? 'instant' : option.bookType,
      });
    }
  }, [
    orderItem?.id,
    orderItem?.supplierActivityOptionId,
    option.id,
    isGiftExperience,
    searchBarParams.numGuest,
    giftExperienceCode?.code,
    isLoadingGiftExperienceCode,
    router.query.transport_id,
    router.query.addons,
  ]);

  useEffect(() => {
    const giftType =
      option.isGiftable && isGiftExperience
        ? 'experience'
        : giftExperienceCode?.id
        ? 'redemption'
        : null;
    setFieldValue('giftType', giftType);
  }, [option.id, isGiftExperience, giftExperienceCode?.id]);

  const currentPrice = useMemo(() => {
    return resolvePrice(prices, option, null, bookingFormValues.startDate, 'foreigner', 'adult', 0);
  }, [option.id]);

  useEffect(() => {
    const event = {
      eventName: option.name,
      contentId: option.id,
      contentName: option.name,
      contentBrand: supplier.name,
      contentCategory: isGiftExperience
        ? `${city?.name} Gift Experience`
        : city && option?.mainTag
        ? `${city.name} ${option.mainTag.name}`
        : undefined,
      contentCategory2: country ? country.name : undefined,
      price: Number(currentPrice.masterPrice.amount),
      currency: MasterCurrency,
      eventId: window.crypto.randomUUID(),
    };
    notify('google', 'ViewContent', event);
    notify('facebook', 'ViewContent', event);
    notify('tiktok', 'ViewContent', event);
    senddFacebookPixelEvent({
      eventName: 'ViewContent',
      eventData: { ...event, optionId: option.id },
    });
  }, [option.id]);

  useEffect(() => {
    const hasAllDetailedTimeslots =
      option.kind === EnumOptionKind.shortAdventure
        ? detailedTimeslots.length >=
          ((detailedTimeslotsData.pagination.totalPages || 1) - 1) * AVAILABILITY_PER_PAGE
        : true;
    const hasAllAvailableDates =
      availableDates.length >=
      ((availableDatesData.pagination.totalPages || 1) - 1) * AVAILABILITY_PER_PAGE;
    if (hasAllAvailableDates && hasAllDetailedTimeslots) {
      const date = availabilityChecker.getScheduledDatesInMonth(
        dayjs().toDate(),
        dayjs().add(6, 'month').toDate()
      )[0];
      if (date) {
        if (option.kind === EnumOptionKind.shortAdventure) {
          setTimeslotDate(date);
          setIsOpenTimeslotSelector(true);
          if (sectionDatepickerRef.current) {
            sectionDatepickerRef.current.setDate([dayjs(date).toDate()]);
          }
        } else {
          if (sectionDatepickerRef.current) {
            try {
              sectionDatepickerRef.current?.jumpToDate(dayjs(date).toDate());
            } catch {}
          }
        }
      }
    }
  }, [
    option.id,
    availableDates.length,
    availableDatesData.pagination.totalPages,
    detailedTimeslots.length,
    detailedTimeslotsData.pagination.totalPages,
  ]);

  return (
    <>
      <NextSeo
        title={metaTitle}
        description={metaDesc}
        canonical={AppRoutes.experienceRoute({ id: option.slugCode }).toUrl()}
        openGraph={{
          title: metaTitle,
          description: metaDesc,
          url: AppRoutes.experienceRoute({ id: option.slugCode }).toUrl(),
          images: option.images[0]?.mdUrl
            ? [
                {
                  url: option.images[0].mdUrl,
                  width: 600,
                  height: 600,
                  alt: option.name,
                },
              ]
            : undefined,
        }}
      />

      <Head>
        <meta property="product:brand" content={option.supplierName} />
        <meta property="product:category" content="Adventure" />
        <meta property="product:availability" content="in stock" />
        <meta property="product:condition" content="new" />
        <meta property="product:price:amount" content={String(currentPrice.masterPrice.amount)} />
        <meta property="product:price:currency" content="SGD" />
        <meta property="product:retailer_item_id" content={`Option #${option.id}`} />
      </Head>

      <WithDefaultJsonLd>
        <ProductJsonLd
          productName={metaTitle}
          images={option.images.map((o) => o.mdUrl)}
          description={metaDesc}
          offers={{
            url: AppRoutes.experienceRoute({ id: option.slugCode }).toUrl(),
            availability: 'https://schema.org/InStock',
            price: String(currentPrice.masterPrice.amount),
            priceCurrency: 'SGD',
          }}
          aggregateRating={{
            ratingValue: supplier.reviewRating,
            reviewCount: supplier.ratedReviewsCount,
          }}
        />
        {option.vimeoIds.length > 0 ? (
          <VideoJsonLd
            name={metaTitle}
            description={metaDesc}
            embedUrl={`https://player.vimeo.com/video/${option.vimeoIds[0]}`}
            thumbnailUrls={option.images.map((o) => o.mdUrl)}
            uploadDate={option.updatedAt}
          />
        ) : null}
      </WithDefaultJsonLd>

      <AdminBar updatedAt={option.updatedAt}>
        <>
          <a
            href={AdminRoutes.adminOptionRoute({ id: option.id }).toUrl()}
            className="btn btn-primary mb-2"
          >
            Edit Option
          </a>
          <a
            href={AdminRoutes.adminOptionsRoute({}).toUrl()}
            className="btn btn-outline-primary mb-2"
          >
            Options Dashboard
          </a>
        </>
      </AdminBar>
      <div className="tw-bg-brand-yellow tw-py-12 md:tw-py-16">
        <div className="container">
          {city ? (
            <Breadcrumb className="breadcrumb--black tw-justify-center tw-mb-5">
              <BreadcrumbItem>
                <Link legacyBehavior prefetch={false} href={AppRoutes.cityRoute({ id: city.slug }).toPath()}>
                  <a>{city.name}</a>
                </Link>
              </BreadcrumbItem>

              {collection ? (
                <BreadcrumbItem>
                  <Link legacyBehavior prefetch={false} href={AppRoutes.collectionRoute({ id: collection.slugCode }).toPath()}>
                    <a>{collection.tag?.name || collection.name}</a>
                  </Link>
                </BreadcrumbItem>
              ) : null}
            </Breadcrumb>
          ) : null}

          <OptionMedia option={option} />

          <div className="tw-mt-8">
            <BasicInfo
              option={option}
              city={city}
              transportItems={transportItems}
              supplier={supplier}
              isGiftExperience={isGiftExperience}
              setShowReviewModal={setShowReviewModal}
            />
          </div>
        </div>
      </div>

      <PageNav
        show={showPageNav}
        option={option}
        hasReview={supplier.ratedReviewsCount > 0}
        accommodationRooms={accommodationRooms}
        availableDates={availableDates}
        itineraryDays={itineraryDays}
        isPublished={isPublished}
      />

      {isMounted && isScreenLg ? <div ref={stickPageNavPositionRef}></div> : null}

      <div className="container lg:tw-pt-16">
        <div className="row row-original tw--mx-3.75">
          <div className="col-lg-8 tw-order-2 lg:tw-order-1 lg:tw-pr-24 tw-px-3.75">
            {isGiftExperience && (
              <div id="gift-experience">
                <div className="tw-text-center md:tw-text-left tw-mx-auto tw-max-w-xs md:tw-max-w-none tw-px-2.5 md:tw-px-0 tw-text-3xl lg:tw-text-4xl tw-font-semibold tw-text-brand-navy tw-font-styleSans tw-mb-8 tw-leading-none lg:tw-leading-none">
                  How this gift experience works:
                </div>

                <div className="row">
                  <div className="col-md-4 tw-flex tw-items-center md:tw-block">
                    <div className="tw-border tw-border-brand-navy tw-rounded-2xl tw-px-4 tw-py-8">
                      <div className="md:tw-mb-2 tw-mr-5 md:tw-mr-0 tw-text-center">
                        <ImageWithFallback
                          src={SuitcaseIcon}
                          className="tw-h-7 tw-w-7 tw-mb-4 tw-inline-block tw-static"
                          alt=""
                        />
                      </div>
                      <div>
                        <div className="tw-font-semibold tw-font-styleSans tw-text-lg md:tw-text-xl tw-text-center tw-leading-none md:tw-leading-none tw-mb-4">
                          Find a Cool Experience
                        </div>
                        <div className="tw-text-sm md:tw-text-base tw-text-brand-navy tw-text-center md:tw-h-28">
                          Choose your gift experience, and when you would like to send your gift.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4 tw-flex tw-items-center md:tw-block tw-mt-10 md:tw-mt-0">
                    <div className="tw-border tw-border-brand-navy tw-rounded-2xl tw-px-4 tw-py-8">
                      <div className="md:tw-mb-2 tw-mr-5 md:tw-mr-0 tw-text-center">
                        <ImageWithFallback
                          src={GiftBoxIcon}
                          className="tw-h-7 tw-w-7 tw-mb-4 tw-inline-block tw-static"
                          alt=""
                        />
                      </div>
                      <div>
                        <div className="tw-font-semibold tw-font-styleSans tw-text-lg md:tw-text-xl tw-text-center tw-leading-none md:tw-leading-none tw-mb-4">
                          Gift it to a Loved One
                        </div>
                        <div className="tw-text-sm md:tw-text-base tw-text-brand-navy tw-text-center md:tw-h-28">
                          Your recipient receives the gift with instructions on how to redeem it.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4 tw-flex tw-items-center md:tw-block tw-mt-10 md:tw-mt-0">
                    <div className="tw-border tw-border-brand-navy tw-rounded-2xl tw-px-4 tw-py-8">
                      <div className="md:tw-mb-2 tw-mr-5 md:tw-mr-0 tw-text-center">
                        <ImageWithFallback
                          src={WatchIcon}
                          className="tw-h-7 tw-w-7 tw-mb-4 tw-inline-block tw-static"
                          alt=""
                        />
                      </div>
                      <div>
                        <div className="tw-font-semibold tw-font-styleSans tw-text-lg md:tw-text-xl tw-text-center tw-leading-none md:tw-leading-none tw-mb-4">
                          They can choose when to go!
                        </div>
                        <div className="tw-text-sm md:tw-text-base tw-text-brand-navy tw-text-center md:tw-h-28">
                          Recipients can choose their own date anytime within a year so it’s totally
                          stress free!
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="tw-my-12 lg:tw-my-16 tw-border-t tw-border-grey-lighter"></div>
              </div>
            )}

            <div id="highlight">
              <div className="tw-text-center md:tw-text-left tw-mx-auto tw-max-w-xs md:tw-max-w-none tw-px-2.5 md:tw-px-0 tw-text-3xl lg:tw-text-4xl tw-font-semibold tw-font-styleSans tw-text-brand-navy tw-mb-4 md:tw-mb-8 tw-leading-none lg:tw-leading-none">
                {option.leadIn}
              </div>

              {option.description && option.strippedDescription ? (
                <ReadMore
                  mobileLength={230}
                  desktopLength={230}
                  as="div"
                  className="simple-rich-doc"
                  fullContent={option.description}
                  content={option.strippedDescription}
                  readMoreClass="tw-block tw-text-brand-navy tw-font-styleSans tw-font-semibold tw-lowercase tw-underline tw-cursor-pointer tw-mt-4 tw-text-center md:tw-text-left"
                />
              ) : null}
            </div>

            {option.highlights &&
            option.highlights.filter((v) => v.images.length > 0).length > 0 ? (
              <>
                <div className="tw-border-t tw-border-grey-lighter tw-my-16"></div>
                <div id="highlights">
                  <div className="tw-text-center md:tw-text-left tw-mx-auto tw-max-w-xs md:tw-max-w-none tw-px-2.5 md:tw-px-0 tw-text-3xl lg:tw-text-4xl tw-font-semibold tw-text-brand-navy tw-font-styleSans tw-mb-8 tw-leading-none lg:tw-leading-none">
                    Experience highlights
                  </div>

                  <div className={sliderStyles.slider}>
                    <Slider
                      slidesToShow={2.3}
                      slidesToScroll={1}
                      dots={false}
                      arrows={true}
                      className={`slider-v2 tw--ml-2 md:tw--ml-3.75`}
                      swipeToSlide={true}
                      infinite={false}
                      prevArrow={
                        <div className="slick-prev">
                          <HiOutlineArrowNarrowLeft
                            size={24}
                            strokeWidth={2}
                            className="tw-text-brand-navy"
                          />
                        </div>
                      }
                      nextArrow={
                        <div className="slick-next">
                          <HiOutlineArrowNarrowRight
                            size={24}
                            strokeWidth={2}
                            className="tw-text-brand-navy"
                          />
                        </div>
                      }
                      responsive={[
                        {
                          breakpoint: 800,
                          settings: {
                            slidesToShow: 2.3,
                            slidesToScroll: 1,
                          },
                        },
                      ]}
                    >
                      {option.highlights
                        .filter((v) => v.images.length > 0)
                        .map((highlight) => (
                          <div key={highlight.id} className="tw-px-2 md:tw-px-3.75 tw-outline-none">
                            <ImageWithFallback
                              record={highlight.images[0] as Image}
                              imgField="webpMdUrl"
                              alt=""
                              className="tw-h-36 md:tw-h-72 tw-w-full tw-object-cover tw-block tw-static tw-border-2 tw-border-brand-green tw-border-solid tw-mb-6 tw-rounded-lg"
                              fill
                            />
                            <div className="tw-text-center tw-font-semibold tw-font-styleSans tw-text-lg md:tw-text-xl tw-leading-none md:tw-leading-none tw-break-words">
                              {highlight.body}
                            </div>
                          </div>
                        ))}
                    </Slider>
                  </div>
                </div>
              </>
            ) : null}

            <ItinerarySection option={option} itineraryDays={itineraryDays} />

            {inclusionsExclusions.length > 0 ? (
              <>
                <div className="tw-border-t tw-border-grey-lighter tw-my-16"></div>
                <div id="inclusion">
                  <div className="tw-text-center md:tw-text-left tw-mx-auto tw-max-w-xs md:tw-max-w-none tw-px-2.5 md:tw-px-0 tw-text-3xl lg:tw-text-4xl tw-font-semibold tw-text-brand-navy tw-font-styleSans tw-mb-8 tw-leading-none lg:tw-leading-none">
                    What’s Included
                  </div>

                  {option.inclusions.length > 0 ? (
                    <div className="row">
                      {inclusionsExclusions
                        .filter((v) => v.kind === 'inclusion')
                        .map((obj, index) => (
                          <div
                            key={index}
                            className={classNames('col-md-6 tw-mb-2', {
                              'tw-hidden': index > 3 && !showAllInclusion,
                            })}
                          >
                            <div className="tw-flex">
                              <CheckInCircleSvg className="tw-h-5 tw-w-5 tw-align-top tw-text-brand-navy tw-mr-2 tw-flex-shrink-0" />
                              <span className={classNames('tw-align-top')}>{obj.text}</span>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : null}

                  {showAllInclusion && option.exclusions.length > 0 ? (
                    <div>
                      <div className="tw-border-t tw-border-grey-lighter tw-my-6"></div>
                      <div className="tw-font-semibold tw-font-styleSans tw-mb-4 md:tw-mb-6 tw-text-xl md:tw-text-2xl">
                        Not included
                      </div>

                      <div className="row">
                        {inclusionsExclusions
                          .filter((v) => v.kind !== 'inclusion')
                          .map((obj, index) => (
                            <div key={index} className={classNames('col-md-6 tw-mb-2')}>
                              <div className="tw-flex">
                                <CloseInCircleSvg className="tw-h-5 tw-w-5 tw-align-top tw-text-brand-navy tw-mr-2 tw-flex-shrink-0" />
                                <span className="tw-align-top">{obj.text}</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  ) : null}

                  {showAllInclusion && (addons.length > 0 || transports.length > 0) ? (
                    <div>
                      <div className="tw-border-t tw-border-grey-lighter tw-my-6"></div>
                      <div className="tw-font-semibold tw-font-styleSans tw-mb-4 md:tw-mb-6 tw-text-xl md:tw-text-2xl">
                        Available Add-ons:
                      </div>
                      {addons.map((addon) => (
                        <div key={addon.id} className={`tw-flex tw-items-center tw-mb-2`}>
                          <HiOutlinePlusCircle
                            strokeWidth={1}
                            size={20}
                            className="tw-align-top tw-text-brand-navy tw-mr-2 tw-flex-shrink-0"
                          />
                          <span>
                            {addon.name} (
                            <Money
                              amount={addon.amount}
                              toCurrency={currentCurrency}
                              hasConversionFee={
                                !isGiftExperience && option.baseCurrency !== currentCurrency
                              }
                            />
                            )
                          </span>
                        </div>
                      ))}
                      {transports.map((transport) => (
                        <div key={transport.id} className={`tw-flex tw-items-center tw-mb-2`}>
                          <HiOutlinePlusCircle
                            strokeWidth={1}
                            size={20}
                            className="tw-align-top tw-text-brand-navy tw-mr-2 tw-flex-shrink-0"
                          />
                          <span>
                            {transport.name} (
                            <Money
                              amount={transport.amount}
                              toCurrency={currentCurrency}
                              hasConversionFee={
                                !isGiftExperience && option.baseCurrency !== currentCurrency
                              }
                            />
                            )
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {option.inclusions.length > 4 ||
                  addons.length > 0 ||
                  transports.length > 0 ||
                  option.exclusions.length > 0 ? (
                    <div className="tw-mt-4 tw-text-center md:tw-text-left">
                      <span
                        className="tw-cursor-pointer tw-underline tw-font-semibold tw-font-styleSans tw-text-sm md:tw-text-base tw-leading-none md:tw-leading-none tw-lowercase"
                        onClick={() => setShowAllInclusion((prev) => !prev)}
                      >
                        {!showAllInclusion ? 'Show' : 'Hide'} Details
                      </span>
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}

            {optionBadges && optionBadges.length > 0 ? (
              <>
                <div className="tw-my-16 tw-border-t tw-border-grey-lighter"></div>
                <div className="tw-text-center md:tw-text-left tw-mx-auto tw-max-w-xs md:tw-max-w-none tw-px-2.5 md:tw-px-0 tw-text-3xl lg:tw-text-4xl tw-font-semibold tw-text-brand-navy tw-font-styleSans tw-mb-8 tw-leading-none lg:tw-leading-none">
                  Who you’re supporting
                </div>
                <div className="tw-flex md:tw-flex-nowrap tw-flex-wrap tw-text-center md:tw-text-left md:tw-justify-start tw-justify-center">
                  {optionBadges.map((o) => {
                    const badge = BADGE_ITEMS[o.kind];
                    return (
                      <OverlayTrigger
                        key={o.kind}
                        placement="top"
                        popperConfig={{
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, 8],
                              },
                            },
                          ]
                        }}
                        overlay={(
                          <div className="tw-bg-brand-green tw-p-4 md:tw-p-6 tw-rounded-lg tw-text-center tw-w-full tw-max-w-xs md:tw-max-w-xl tw-text-sm md:tw-text-base">
                            {o.description || badge.defaultDescription}
                          </div>
                        )}
                      >
                        <div
                          className="tw-text-center md:tw-w-auto tw-w-28 md:tw-mr-6 tw-mr-0 tw-mb-8"
                        >
                          <ImageWithFallback
                            src={badge.image}
                            alt={o.kind}
                            className="tw-w-14 tw-h-14 tw-mb-2"
                          />
                          <div className="tw-text-sm tw-font-semibold md:tw-w-24 tw-w-full">
                            {badge.name}
                          </div>
                        </div>
                      </OverlayTrigger>
                    );
                  })}
                </div>
                <div
                  className="tw-font-semibold tw-font-styleSans tw-lowercase tw-underline tw-cursor-pointer tw-mt-6 tw-text-center md:tw-text-left"
                  onClick={() => {
                    setShowBadgeModal(true);
                  }}
                >
                  Show Details
                </div>
                <Modal
                  show={showBadgeModal}
                  className="modal-styled"
                  onHide={() => {
                    setShowBadgeModal(false);
                  }}
                >
                  <Modal.Header>
                    <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
                      <div className="tw-text-xl md:tw-text-2xl tw-font-semibold tw-font-styleSans tw-leading-none md:tw-leading-none">
                        Who you’re supporting
                      </div>
                      <FiX
                        size={24}
                        className="tw-cursor-pointer"
                        onClick={() => setShowBadgeModal(false)}
                      />
                    </div>
                  </Modal.Header>
                  <Modal.Body className="tw-pt-8 tw-pb-14">
                    {optionBadges.map((o, index) => {
                      const badge = BADGE_ITEMS[o.kind];
                      return (
                        <div key={o.kind}>
                          <div className="md:tw-flex tw-flex-row md:tw-items-center md:tw-justify-start tw-justify-center">
                            <div className="tw-text-center tw-mb-2">
                              <ImageWithFallback
                                src={badge.image}
                                alt={o.kind}
                                className="md:tw-w-16 md:tw-h-16 tw-w-14 tw-h-14"
                              />
                            </div>
                            <div className="md:tw-ml-6 tw-ml-0 md:tw-text-left tw-text-center">
                              <div className="tw-text-sm md:tw-text-lg md:tw-mb-0 tw-mb-2 tw-font-semibold md:tw-font-styleSans tw-font-sans">
                                {badge.name}
                              </div>
                              <div>
                                {o.description ? o.description : badge.defaultDescription}
                              </div>
                            </div>
                          </div>
                          <div
                            className={classNames('tw-my-6 tw-border-t tw-border-grey-lighter', {
                              'tw-hidden': index + 1 === optionBadges.length,
                            })}
                          ></div>
                        </div>
                      );
                    })}
                  </Modal.Body>
                </Modal>
              </>
            ) : (
              ''
            )}

            {accommodationRooms.length > 0 ? (
              <>
                <div className="tw-my-16 tw-border-t tw-border-grey-lighter"></div>
                <div id="accommodation" className="tw-mt-8">
                  <div className="tw-text-center md:tw-text-left tw-mx-auto tw-max-w-xs md:tw-max-w-none tw-px-2.5 md:tw-px-0 tw-text-3xl lg:tw-text-4xl tw-font-semibold tw-text-brand-navy tw-font-styleSans tw-mb-8 tw-leading-none lg:tw-leading-none">
                    Where You’ll Sleep
                  </div>

                  <OptionAccommodationRoomCarousel
                    isGiftExperience={isGiftExperience}
                    option={option}
                    accommodationRooms={accommodationRooms}
                    availableDates={availableDates}
                    startDate={bookingFormValues.startDate}
                  />
                </div>
              </>
            ) : null}

            {isPublished && !isGiftExperience ? (
              <>
                <div className="tw-my-12 lg:tw-my-16 tw-border-t tw-border-grey-lighter"></div>
                <div id="availability" className="tw-mt-8">
                  <div className="tw-text-center md:tw-text-left tw-mx-auto tw-max-w-xs md:tw-max-w-none tw-px-2.5 md:tw-px-0 tw-text-3xl lg:tw-text-4xl tw-font-semibold tw-text-brand-navy tw-font-styleSans tw-mb-8 tw-leading-none lg:tw-leading-none">
                    Next Available Dates
                  </div>

                  <OptionDatepicker
                    instanceRef={sectionDatepickerRef}
                    availableDates={availableDates}
                    availableDatesNoDurationConstraint={[]}
                    option={option}
                    prices={prices}
                    currentCurrency={currentCurrency}
                    bookingFormValues={bookingFormValues}
                    detailedTimeslots={detailedTimeslots}
                    viewOnly={true}
                    onClick={(date, dayEle) => {
                      if (option.kind === EnumOptionKind.shortAdventure) {
                        setTimeslotDate(date);
                        setIsOpenTimeslotSelector(true);
                        if (sectionDatepickerRef.current) {
                          sectionDatepickerRef.current.setDate([dayjs(date).toDate()]);
                        }
                      } else {
                        setSectionDatepickerDate(date);
                        dayEle.blur();
                      }
                    }}
                  />
                  {!giftExperienceCode && (
                    <div className="tw-text-sm tw-text-grey tw-mt-4 tw-pl-2 tw-text-center md:tw-text-left">
                      Prices shown in {currentCurrency}
                    </div>
                  )}
                  <BookingTimeslotSelectorV2
                    className="tw-mt-4 tw-pl-2 tw-text-center md:tw-text-left tw-just"
                    slotContainerClassName="tw-justify-center md:tw-justify-start"
                    key={`section-${timeslotDate}`}
                    show={isOpenTimeslotSelector && !!timeslotDate}
                    timeslotDate={timeslotDate}
                    timeslots={
                      timeslotDate
                        ? availabilityChecker.getAvailableTimeslotByDate(timeslotDate)
                        : []
                    }
                    onSelect={(timeslot) => {
                      setSectionDetailedTimeslotId(timeslot.id);
                      setIsOpenTimeslotSelector(false);
                      setTimeslotDate(undefined);
                      sectionDatepickerRef.current?.setDate('');
                    }}
                  />
                </div>
              </>
            ) : null}

            <div className="tw-my-12 lg:tw-my-16 tw-border-t tw-border-grey-lighter"></div>
            <div id="location">
              <div className="tw-text-center md:tw-text-left tw-mx-auto tw-max-w-xs md:tw-max-w-none tw-px-2.5 md:tw-px-0 tw-text-3xl lg:tw-text-4xl tw-font-semibold tw-text-brand-navy tw-font-styleSans tw-mb-8 tw-leading-none lg:tw-leading-none">
                Additional Information
              </div>

              {transportItems.length > 0 ? (
                <AccordionFaq
                  keyName={`location`}
                  defaultShow={true}
                  renderTitle={() => (
                    <div className="tw-font-semibold tw-font-styleSans tw-text-xl md:tw-text-2xl tw-leading-none md:tw-leading-none">
                      Where we{"'"}ll meet
                    </div>
                  )}
                >
                  {transportItems.length > 0 ? (
                    <>
                      {transportItems[0]?.startPointLatitude ? (
                        <div className="tw-h-72 tw-rounded-lg tw-border-2 tw-border-brand-green tw-relative tw-mb-5">
                          <TransportItemMap
                            transportItem={transportItems[0]}
                            location={{
                              latitude: transportItems[0].startPointLatitude,
                              longitude: transportItems[0].startPointLongitude,
                            }}
                          />
                        </div>
                      ) : null}

                      <div className="tw-relative tw-pb-6">
                        <div className="tw-absolute tw-h-full tw-border-r tw-border-black"></div>
                        <div className="tw-absolute tw-z-10 tw--left-1">
                          <div className="tw-rounded-full tw-bg-white tw-border tw-border-purple tw-p-1"></div>
                        </div>
                        <div className="tw-pl-6">
                          <div className="tw-font-semibold tw-font-styleSans tw-text-lg md:tw-text-xl tw-mb-3">
                            Start Point
                          </div>
                          {transportItems.slice(0, 1).map((transportItem, index) => (
                            <div key={transportItem.id} className={index > 0 ? 'tw-mt-3' : ''}>
                              <div className="tw-text-brand-navy tw-mb-2">
                                {transportItem.kind === EnumTransportItemKind.meetup
                                  ? 'Meet-up at '
                                  : 'Free pick-up from '}
                                {transportItem.startPointDescription}
                                {transportItem.startPointAddress ? (
                                  <span className="tw-text-grey tw-ml-1">
                                    ({transportItem.startPointAddress})
                                  </span>
                                ) : (
                                  ''
                                )}
                              </div>
                              <ul className="tw-pl-6 tw-text-brand-navy tw-mb-0">
                                {transportItem.startPointTime ? (
                                  <li>{transportItem.startPointTime}</li>
                                ) : null}
                                {transportItem.note ? (
                                  <li className="tw-mt-1">Notes: {transportItem.note}</li>
                                ) : null}
                              </ul>
                            </div>
                          ))}
                          {transportItems.length > 1 ? (
                            <div
                              onClick={() => setShowLocationModal(true)}
                              className="tw-underline tw-cursor-pointer tw-text-brand-navy tw-mt-3"
                            >
                              +{pluralize('other option', transportItems.length - 1, true)}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {endPoints.length > 0 ? (
                        <div className="tw-relative">
                          <div className="tw-absolute tw-z-10 tw--left-2.5 tw--top-0.5">
                            <MapPinSvg width={20} height={20} className="tw-text-purple" />
                          </div>
                          <div className="tw-pl-6">
                            <div className="tw-font-semibold tw-mb-3">End Point</div>
                            <ul className="tw-pl-6">
                              {endPoints.slice(0, 1).map((endPoint, index) => (
                                <li
                                  key={endPoint.id}
                                  className={`tw-text-brand-navy ${index > 0 ? 'tw-mt-3' : ''}`}
                                >
                                  End at {endPoint.description} ({endPoint.address})
                                </li>
                              ))}
                            </ul>

                            {endPoints.length > 1 ? (
                              <div
                                onClick={() => setShowLocationModal(true)}
                                className="tw-underline tw-cursor-pointer tw-text-brand-navy tw-mt-3"
                              >
                                +{pluralize('other option', endPoints.length - 1, true)}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : (
                        <div className="tw-relative">
                          <div className="tw-absolute tw-z-10 tw--left-2.5 tw--top-0.5">
                            <MapPinSvg width={20} height={20} className="tw-text-purple" />
                          </div>
                          <div className="tw-pl-6">
                            <div className="tw-font-semibold tw-mb-3">End Point</div>
                            <ul className="tw-pl-6">
                              <li className="tw-text-brand-navy">End at Start Point(s)</li>
                            </ul>
                          </div>
                        </div>
                      )}
                    </>
                  ) : null}

                  {transportItems.length === 1 && transports.length > 0 ? (
                    <>
                      <div className="tw-font-semibold tw-text-xl tw-mb-4 tw-mt-6">
                        Transport Add-ons
                      </div>
                      {transports.map((transport, index) => (
                        <div className={index > 0 ? 'tw-mt-6' : ''} key={transport.id}>
                          <div className="tw-font-semibold tw-text-brand-navy tw-leading-none tw-mb-2">
                            Add-on #{index + 1}
                          </div>
                          <ul className="tw-pl-5 tw-mb-0">
                            <li>
                              {transport.name}
                              (Price:{' '}
                              <Money
                                amount={transport.amount}
                                toCurrency={currentCurrency}
                                hasConversionFee={
                                  !isGiftExperience && option.baseCurrency !== currentCurrency
                                }
                              />
                              {transport.capacity
                                ? ` for up to ${pluralize('guest', transport.capacity, true)}`
                                : ''}
                              )
                            </li>
                          </ul>
                        </div>
                      ))}
                    </>
                  ) : null}

                  <ModalTransportItems
                    isGiftExperience={isGiftExperience}
                    transportItems={transportItems}
                    endPoints={endPoints}
                    transports={transports}
                    show={showLocationModal}
                    setShow={setShowLocationModal}
                  />
                </AccordionFaq>
              ) : null}

              {option.kind === EnumOptionKind.adventure && transportItems.length === 0 ? (
                <></>
              ) : (
                <AccordionFaq
                  keyName={`start-end`}
                  renderTitle={() => (
                    <div className="tw-font-semibold tw-font-styleSans tw-text-xl md:tw-text-2xl tw-leading-none md:tw-leading-none">
                      Start & ends
                    </div>
                  )}
                >
                  <StartEndBlock
                    option={option}
                    timeslots={timeslots}
                    transportItems={transportItems}
                  />
                </AccordionFaq>
              )}

              <AccordionFaq
                keyName={`start-end`}
                renderTitle={() => (
                  <div className="tw-font-semibold tw-font-styleSans tw-text-xl md:tw-text-2xl tw-leading-none md:tw-leading-none">
                    Suitability / Accessibility
                  </div>
                )}
              >
                <ul className="tw-pl-5">
                  {suitableTags.map((tag, index) => (
                    <li key={tag.id} className={`tw-text-brand-navy ${index > 0 ? 'tw-mt-3' : ''}`}>
                      {tag.name}
                    </li>
                  ))}
                  <li className="tw-mt-3 tw-text-brand-navy">
                    {option.minAge
                      ? option.minAge <= 12
                        ? `Child Friendly (Recommended Age: ${option.minAge}+)`
                        : `Recommended Age: ${option.minAge}+`
                      : 'Child Friendly (No Minimum Age)'}
                  </li>
                </ul>
              </AccordionFaq>

              <AccordionFaq
                keyName={`cancellation`}
                renderTitle={() => (
                  <div className="tw-font-semibold tw-font-styleSans tw-text-xl md:tw-text-2xl tw-leading-none md:tw-leading-none">
                    Cancellation & other policies
                  </div>
                )}
              >
                <CancellationBlock option={option} />
              </AccordionFaq>

              {option.packings && option.packings.length > 0 ? (
                <AccordionFaq
                  className="tw-py-5"
                  keyName="packings"
                  renderTitle={() => (
                    <div className="tw-font-semibold tw-font-styleSans tw-text-xl md:tw-text-2xl tw-leading-none md:tw-leading-none">
                      What to Pack
                    </div>
                  )}
                >
                  <ul className="tw-flex tw-flex-wrap tw-pl-5">
                    {option.packings.map((packing) => (
                      <li key={packing} className="col-md-6 tw-mb-2 tw-leading-none">
                        {packing}
                      </li>
                    ))}
                  </ul>
                </AccordionFaq>
              ): null}
            </div>
          </div>

          <div className="col-lg-4 tw-order-1 lg:tw-order-2 tw-px-3.75 tw-bg-brand-yellow lg:tw-bg-transparent tw-pt-0 lg:tw-py-0 tw-pb-12 tw-mb-12 lg:tw-mb-0">
            {isPublished ? (
              <div
                id="booking-form-container"
                className={classNames(
                  'booking-form tw-p-4 tw-rounded-lg md:tw-sticky tw-bg-white lg:tw-bg-brand-yellow tw-border tw-border-brand-green',
                  {
                    'md:tw-top-20': !showHeader || !showPageNav,
                    'md:tw-top-40': showHeader && showPageNav,
                  }
                )}
              >
                {availableDates.length > 0 || isGiftExperience ? (
                  <BookingForm
                    currentCurrency={currentCurrency}
                    option={option}
                    supplier={supplier}
                    availableDates={availableDates}
                    availableDatesNoDurationConstraint={availableDatesNoDurationConstraint}
                    isGiftExperience={isGiftExperience}
                    giftExperienceCode={giftExperienceCode}
                    accommodationRooms={accommodationRooms}
                    transports={transports}
                    addons={addons}
                    prices={prices}
                    detailedTimeslots={detailedTimeslots}
                    showBookingBottomBar={showBookingBottomBar}
                    sectionDatepickerDate={sectionDatepickerDate}
                    setSectionDatepickerDate={setSectionDatepickerDate}
                    sectionDetailedTimeslotId={sectionDetailedTimeslotId}
                    setSectionDetailedTimeslotId={setSectionDetailedTimeslotId}
                    addToCartError={addToCartError}
                    isLoadingAvailability={isLoadingAvailability}
                    setShowReviewModal={setShowReviewModal}
                  />
                ) : (
                  <WaitListForm
                    option={option}
                    supplier={supplier}
                    setShowReviewModal={setShowReviewModal}
                  />
                )}
              </div>
            ) : (
              <div className="tw-p-4 tw-bg-white md:tw-bg-brand-yellow tw-rounded-lg tw-border-2 tw-border-brand-green">
                Uh-oh, that adventure isn{"'"}t available right now. Let{"'"}s find you something
                else awesome!
              </div>
            )}

            {isMounted && !isScreenLg ? <div ref={stickPageNavPositionRef}></div> : null}
          </div>
        </div>

        <div
          id="host"
          className="tw-bg-brand-yellow tw--mx-3.75 md:tw-mx-0 tw-px-6 tw-py-16 md:tw-p-8 tw-mt-16 md:tw-mb-16 tw-rounded-lg"
        >
          <div className="lg:tw-flex tw-items-stretch">
            <div>
              <div className="tw-relative tw-h-60 lg:tw-h-full tw-w-full lg:tw-w-72 tw-flex-shrink-0 lg:tw-mr-16 tw-mx-auto tw-mb-8 lg:tw-mb-0">
                <ImageWithFallback
                  src={supplier.ologo?.webpMdUrl || SupplierDefaultImg}
                  fill
                  alt=""
                  className={`tw-object-cover tw-w-full tw-h-full tw-border-2 tw-border-brand-green tw-border-solid tw-rounded-lg`}
                />
              </div>
            </div>
            <div>
              <div className="tw-font-semibold tw-font-styleSans tw-mb-6 tw-text-3xl md:tw-text-4xl tw-leading-none md:tw-leading-none tw-text-center md:tw-text-left">
                Meet your host
              </div>
              {supplier.description && supplier.strippedDescription ? (
                <div className="tw-mb-4 tw-text-center md:tw-text-left">
                  <ReadMore
                    mobileLength={320}
                    desktopLength={320}
                    as="div"
                    className="simple-rich-doc"
                    fullContent={supplier.description}
                    content={supplier.strippedDescription}
                    readMoreClass="tw-block tw-text-brand-navy tw-font-styleSans tw-font-semibold tw-lowercase tw-underline tw-cursor-pointer tw-mt-4 tw-text-center md:tw-text-left"
                  />
                </div>
              ) : null}

              <div className="row tw-mb-6 tw-text-center md:tw-text-left">
                {supplier.experience ? (
                  <div className="col-md-6 tw-text-grey tw-text-sm tw-mb-2">
                    Experience: {pluralize('year', supplier.experience, true)}
                  </div>
                ) : null}
                {supplier.language ? (
                  <div className="col-md-6 tw-text-grey tw-text-sm tw-mb-2">
                    Speaks: {supplier.language}
                  </div>
                ) : null}
                {supplier.acceptanceRate ? (
                  <div className="col-md-6 tw-text-grey tw-text-sm tw-mb-2">
                    Acceptance rate: {supplier.acceptanceRate}%
                  </div>
                ) : null}
                {supplier.reviewRating > 4.6 && supplier.ratedReviewsCount > 30 ? (
                  <div className="col-md-6 tw-text-grey tw-text-sm tw-mb-2">Top Rated Host</div>
                ) : null}
              </div>

              {option.isPublished ? (
                <>
                  <div className="tw-mb-6 tw-text-center md:tw-text-left">
                    <button className="btn btn-primary" disabled={isCreatingChatConversation} onClick={() => {
                      if (currentUser) {
                        chatWithSupplier()
                      } else {
                        setIsTouched(true)
                        setShowModalLogin(true)
                      }
                    }}>
                      message host
                    </button>
                  </div>

                  <Modal
                    show={showMessageHostModal}
                    onHide={() => setShowMessageHostModal(false)}
                    className="tw-pr-0"
                    dialogClassName="modal-dialog-centered"
                  >
                    <Modal.Header>
                      <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
                        <div className="tw-text-xl md:tw-text-2xl tw-font-semibold tw-font-styleSans tw-leading-none md:tw-leading-none">
                          Message Your Host
                        </div>
                        <FiX
                          size={24}
                          className="tw-cursor-pointer"
                          onClick={() => setShowMessageHostModal(false)}
                        />
                      </div>
                    </Modal.Header>
                    <Modal.Body>
                      <ImageWithFallback
                        src={supplier.ologo?.webpMdUrl || SupplierDefaultImg}
                        fill
                        alt=""
                        className={`tw-mx-auto tw-block tw-static tw-object-cover tw-h-24 tw-w-24 tw-border-2 tw-border-brand-green tw-border-solid tw-rounded-full tw-mb-4`}
                      />
                      <div className="tw-font-styleSans tw-font-semibold tw-leading-none tw-text-xl tw-mb-2 tw-text-center">{supplier.name}</div>
                      <div className="tw-text-grey tw-leading-none tw-text-center tw-mb-4">Usually responds within {displaySupplierResponseTime(supplier.avgResponseTimeInSeconds)}</div>

                      <div className="tw-bg-white tw-py-3 tw-px-4 tw-rounded-lg tw-border-brand-green tw-border tw-text-center">
                        <div>Ask a question about this experience</div>
                        <div className="tw-font-semibold">{option.name}</div>
                      </div>
                    </Modal.Body>
                    <Modal.Footer className="tw-justify-center">
                      <div>
                        <button type="button" className="btn btn-primary" onClick={() => {
                          window.open(AppRoutes.chatConversationRoute({ id: chatConversation?.id as number }, { chat_inquiry_id: chatConversation?.chatInquiryId }).toUrl(), '_blank')
                        }}>start conversation</button>
                      </div>
                    </Modal.Footer>
                  </Modal>
                </>
              ) : null}

              <div className="tw-flex tw-items-center tw-bg-grey-lightest tw-border-2 tw-border-brand-green tw-rounded-lg tw-px-8 tw-py-2.5">
                <ImageWithFallback
                  src={ShieldIcon}
                  className="tw-h-8 tw-w-auto tw-object-cover tw-hidden md:tw-block tw-static tw-mr-7"
                  alt=""
                />
                <div className="tw-text-sm md:tw-text-base">
                  Keep your payments secure by using the Seek Sophie website for all transactions
                  and communications.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {supplier.ratedReviewsCount > 0 && (
        <div id="review">
          <ReviewSectionV2
            key={option.id}
            supplier={supplier}
            optionReviewPagination={optionReviewPagination}
            option={option}
            showModal={showReviewModal}
            setShowModal={setShowReviewModal}
          />
        </div>
      )}

      {false ? (
        <div className="tw-bg-grey-lightest tw-py-16 md:tw-py-24">
          <div id="faqs" className="container tw-max-w-3xl">
            <UserFaqSection
              key={isGiftExperience ? 1 : option.id}
              faqs={isGiftExperience ? GIFT_EXPERIENCE_FAQS : faqs}
              limit={isGiftExperience ? GIFT_EXPERIENCE_FAQS.length : 5}
              faqPagination={isGiftExperience ? DEFAULT_PAGINATION : faqPagination}
              title={isGiftExperience ? 'Gift Experience FAQs' : 'Helpful Questions'}
              hiddenAskQuestion={isGiftExperience}
              faqable={{ id: String(option.id), type: 'SupplierActivityOption' }}
              isAdmin={currentUser?.isAdmin || false}
            />
          </div>
        </div>
      ) : null}

      {!isGiftExperience ? (
        <>
          {typeof similarOptions === 'undefined' ? (
            <Loading className="tw-justify-center tw-py-14" />
          ) : (
            <div className="tw-mt-16">
              <ScrollableCollectionOptions
                totalCount={similarOptions.length}
                options={similarOptions}
                collection={{
                  id: option.id,
                  name: option.name,
                  slug: option.slug,
                }}
                infinite={false}
                useHtmlLink={true}
                renderTitle={() => (
                  <h2 className="tw-text-center md:tw-text-left tw-max-w-xs md:tw-max-w-full tw-mx-auto tw-font-semibold tw-font-styleSans tw-text-2xl md:tw-text-4xl tw-leading-none md:tw-leading-none tw-mb-0">
                    You May Also Like
                  </h2>
                )}
              />
            </div>
          )}

          {typeof supplierOtherOptions === 'undefined' ? (
            <Loading className="tw-justify-center tw-py-14" />
          ) : (
            <ScrollableCollectionOptions
              totalCount={supplierOtherOptions.length}
              options={supplierOtherOptions}
              collection={{
                id: option.id,
                name: option.name,
                slug: option.slug,
              }}
              infinite={false}
              useHtmlLink={true}
              renderTitle={() => (
                <h2 className="tw-text-center md:tw-text-left tw-max-w-xs md:tw-max-w-full tw-mx-auto tw-font-semibold tw-font-styleSans tw-text-2xl md:tw-text-4xl tw-leading-none md:tw-leading-none tw-mb-0">
                  More From This Host
                </h2>
              )}
            />
          )}

          {location ? (
            typeof locationOptions === 'undefined' ? (
              <Loading className="tw-justify-center tw-py-14" />
            ) : (
              <ScrollableCollectionOptions
                totalCount={locationOptions.length}
                options={locationOptions}
                collection={{
                  id: option.id,
                  name: option.name,
                  slug: option.slug,
                }}
                infinite={false}
                useHtmlLink={true}
                renderTitle={() => (
                  <h2 className="tw-text-center md:tw-text-left tw-max-w-xs md:tw-max-w-full tw-mx-auto tw-font-semibold tw-font-styleSans tw-text-2xl md:tw-text-4xl tw-leading-none md:tw-leading-none tw-mb-0">
                    More From {location.name}
                  </h2>
                )}
              />
            )
          ) : null}
        </>
      ) : null}

      <Homework />

      {isGiftExperience ? (
        <div className="tw-bg-brand-yellow tw-py-16 md:tw-py-24">
          <div className="container">
            <div className="tw-flex tw-justify-center tw-w-full tw-font-styleSans tw-text-5xl md:tw-text-6xl tw-font-semibold tw-mb-12">
              <div className="md:tw-w-auto tw-w-60 tw-text-center">2 Awesome Ways to Gift</div>
            </div>
            <div className="row">
              <div className="col-md-5 tw-text-center">
                <ImageWithFallback
                  src={Illu2}
                  alt=""
                  className="tw-w-auto tw-h-44 tw-inline-block tw-static"
                />
                <div className="tw-font-styleSans tw-font-semibold tw-text-3xl md:tw-text-4xl tw-leading-none md:tw-leading-none tw-my-4 md:tw-my-6">
                  Give an Experience
                  <br /> They’ll Love
                </div>
                <div>Choose an experience they’ll love and let them pick the date later!</div>
                <div className="tw-mt-4 md:tw-mt-6">
                  <Link legacyBehavior prefetch={false} href={giftCollection.slugCode ? AppRoutes.collectionRoute({ id: giftCollection.slugCode }).toPath() : '#'}>
                    <a className="btn btn-primary tw-normal-case">see {country?.name} gift experiences</a>
                  </Link>
                </div>
              </div>
              <div className="offset-md-2 col-md-5 tw-text-center tw-mt-16 md:tw-mt-0">
                <ImageWithFallback
                  src={Illu3}
                  alt=""
                  className="tw-w-auto tw-h-44 tw-inline-block tw-static"
                />
                <div className="tw-font-styleSans tw-font-semibold tw-text-3xl md:tw-text-4xl tw-leading-none md:tw-leading-none tw-my-4 md:tw-my-6">
                  Give the Best Gift <br />
                  Card Ever
                </div>
                <div>
                  Give them freedom to decide from over 2500 unique experiences across Asia.
                </div>
                <div className="tw-mt-4 md:tw-mt-6">
                  <Link legacyBehavior prefetch={false} href={AppRoutes.giftsSendRoute().toUrl()}>
                    <a className="btn btn-primary">get them a gift card</a>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : city ? (
        <Banner
          title={`Top things to do in ${city.name}`}
          image={city.image}
          url={AppRoutes.cityRoute({ id: city.slug }).toUrl()}
          urlLabel={`explore ${city.name}`}
        />
      ) : null}
    </>
  );
};

const Page: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = (props) => {
  const addToCart = useEnhanceApiClient(ApiClient.addToCart);
  const increaseViewCount = useEnhanceApiClient(ApiClient.increaseViewCount);
  const router = useRouter();
  const currentCurrency = useCurrentCurrency();
  const [addToCartError, setAddToCartError] = useState<string>();
  const [currentCart, setCurrentCart] = useCart();
  const { giftExperienceCode } = useGiftExperienceCode();

  useEffect(() => {
    if (props.option.id) {
      increaseViewCount({
        view_countable_id: props.option.id,
        view_countable_type: 'SupplierActivityOption',
      });
    }
  }, [props.option.id]);

  return (
    <Formik<BookingFormValues>
      validate={(values) => {
        let errors = {};
        const numPersonDiff =
          getTotalQty(values) -
          values.orderItemAccommodationRooms.reduce((total, o) => total + o.numOfPerson * o.qty, 0);
        try {
          BookingFormValidationSchema.validateSync(values, {
            abortEarly: false,
            context: {
              option: props.option,
              isGiftExperience: router.query.gift_experience === '1',
              numPersonDiff:
                props.option.accommodationSaleType === EnumOptionAccommodationSaleType.pickRoom
                  ? numPersonDiff
                  : undefined,
            },
          });
        } catch (error) {
          if (error instanceof ValidationError) {
            const strError = error.inner
              .map((obj) => {
                return obj.path ? qs.stringify({ [obj.path]: obj.message }) : null;
              })
              .filter((v) => v)
              .join('&');
            const objError = qs.parse(strError, { allowDots: true, allowSparse: true });
            errors = { ...errors, ...objError };
          }
        }
        return errors;
      }}
      initialValues={{
        supplierActivityOptionId: props.option.id,
        quantity: 0,
        childQuantity: 0,
        localQuantity: 0,
        childLocalQuantity: 0,
        hasLocal: false,
        addons: [],
        orderItemAccommodationRooms: props.accommodationRooms.map((o) => ({
          accommodationRoomName: o.name,
          numOfPerson: 0,
          maxNumOfPerson: o.maxNumOfPerson,
          accommodationRoomId: o.id,
          qty:
            props.option.accommodationSaleType === EnumOptionAccommodationSaleType.pickRoom ? 0 : 1,
          roomQty: o.qty,
          masterAmount: ZERO_AMOUNT,
          amount: ZERO_AMOUNT,
        })),
        transportId: null,
        currency: currentCurrency,
        detailedTimeslotId: null,
        startDate: null,
        endDate: null,
        giftExperience: false,
        bookType: props.option.bookType,
        giftType: null,
      }}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          const [cart] = await addToCart({
            order_item: {
              ...values,
              next_day_booking: dayjs(values.startDate).isSameOrBefore(dayjs().add(1, 'day')),
              addons: values.addons.map((o) => ({ id: o.addonId, qty: o.qty })),
            },
            view: 'with_order_item',
            cart_id: currentCart.id,
          });
          if ('error' in cart) {
            setAddToCartError(cart.error);
          } else {
            setCurrentCart(cart);
            router.push(AppRoutes.checkoutRoute({}, { gdc: giftExperienceCode?.code }).toPath());
          }
          setSubmitting(false);
        } catch (e) {
          setSubmitting(false);
          captureException(e);
        }
      }}
    >
      <ModalAccommodationRoomProvider>
        <OptionShow {...props} addToCartError={addToCartError} />
      </ModalAccommodationRoomProvider>
    </Formik>
  );
};

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<OptionPage> = async (context) => {
  if (!context.params?.id) {
    return {
      notFound: true,
    };
  }

  const [data] = await ApiClient.getOptionPage({ id: String(context.params.id) });

  if ('error' in data) {
    return {
      notFound: true,
    };
  }

  return {
    props: data,
    revalidate: RevalidateSettings.veryShort,
  };
};

export default Page;
